import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, FormArray, AbstractControl, Validators } from '@angular/forms';
import { PageHeaderComponent } from '../../shared/ui/components';
import { HlmButton } from '@spartan-ng/helm/button';
import { HlmBadge } from '@spartan-ng/helm/badge';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { HlmIcon } from '@spartan-ng/helm/icon';
import {
  lucideX, lucideCheckCircle, lucideAlertTriangle,
  lucideInfo, lucideClipboard, lucideSettings, lucideWifi, lucideWand2,
  lucideTrash2, lucideShieldCheck, lucidePlusCircle, lucideRefreshCw,
  lucidePlayCircle, lucideUserPlus, lucideKey, lucideShieldOff,
  lucideShieldPlus, lucidePlus, lucidePencil, lucideArrowUpCircle,
  lucideArrowDownCircle, lucideMoreVertical, lucideStar, lucideShieldAlert,
  lucideBot, lucideUserMinus, lucideUsers, lucideLock, lucideLink,
  lucideZap, lucideGlobe, lucideEye, lucideClock, lucideArrowUpRight,
  lucideShoppingBag, lucideAnchor, lucidePlane, lucidePackage,
  lucideMinusCircle, lucideCircleHelp,
} from '@ng-icons/lucide';
import { Subject, timer, interval } from 'rxjs';
import { takeUntil, finalize, take, tap } from 'rxjs/operators';

// Services
import { IntegrationService, ClientIntegrationDto, ClientType, ClientStatus, ClientUserDto, ClientEndpointDto, ClientCredentialsDto, GenerateCredentialsResponse, EnhancedClientSecretsDto } from '../../services/integration.service';
import { ClientIntegrationService } from '../../services/client-integration.service';
import { AuthService } from '../../services/auth.service';
import { ClientIntegrationTestingComponent } from './client-integration-testing/client-integration-testing.component';
import { ClientAuditLogsComponent } from './client-audit-logs/client-audit-logs.component';
import { SystemFeatureOption, BulkUpdateEndpointsRequest, BulkUpdateEndpointsResponse } from '../../models/security.models';

// Define interfaces for UI options
interface ProviderOption {
  value: ClientType;
  label: string;
  icon: string;
}

interface StatusOption {
  value: ClientStatus;
  label: string;
  class: string;
}

interface RoleOption {
  value: string;
  label: string;
  description: string;
}

interface EndpointTypeOption {
  value: string;
  label: string;
  description: string;
}

interface HttpMethodOption {
  value: string;
  label: string;
}

interface CredentialTypeOption {
  value: string;
  label: string;
  description: string;
  icon: string;
}

interface CredentialDirection {
  value: 'INBOUND' | 'OUTBOUND';
  label: string;
  description: string;
  icon: string;
}

@Component({
  selector: 'app-integration-management',
  standalone: true,
  imports: [
    CommonModule, FormsModule, ReactiveFormsModule,
    ClientIntegrationTestingComponent, ClientAuditLogsComponent,
    PageHeaderComponent, HlmButton, HlmBadge, ...HlmCardImports,
    NgIcon, HlmIcon,
  ],
  providers: [provideIcons({
    lucideX, lucideCheckCircle, lucideAlertTriangle,
    lucideInfo, lucideClipboard, lucideSettings, lucideWifi, lucideWand2,
    lucideTrash2, lucideShieldCheck, lucidePlusCircle, lucideRefreshCw,
    lucidePlayCircle, lucideUserPlus, lucideKey, lucideShieldOff,
    lucideShieldPlus, lucidePlus, lucidePencil, lucideArrowUpCircle,
    lucideArrowDownCircle, lucideMoreVertical, lucideStar, lucideShieldAlert,
    lucideBot, lucideUserMinus, lucideUsers, lucideLock, lucideLink,
    lucideZap, lucideGlobe, lucideEye, lucideClock, lucideArrowUpRight,
    lucideShoppingBag, lucideAnchor, lucidePlane, lucidePackage,
    lucideMinusCircle, lucideCircleHelp,
  })],
  templateUrl: './integration-management.component.html',
  styleUrl: './integration-management.component.css'
})
export class IntegrationManagementComponent implements OnInit, OnDestroy {
  // Component state
  private destroy$ = new Subject<void>();
  integrationClients: ClientIntegrationDto[] = [];
  selectedClient: ClientIntegrationDto | null = null;
  
  // Loading states
  isLoading = false;
  isSaving = false;
  isTesting = false;

  // UI states
  showCreateClient = false;
  showEditClient = false;
  showInviteUser = false;
  showEndpointModal = false;
  showSecretsModal = false;
  showCredentialsModal = false;
  showCredentialsInfo = false;
  activeTab = 'overview'; // Add tab management

  // Secrets modal state
  selectedCredentialDirection: 'INBOUND' | 'OUTBOUND' = 'OUTBOUND';
  selectedCredentialType = 'api_key';

  // JWT secret state - single source of truth
  jwtSecretState = {
    secret: '',
    isVisible: false,
    countdown: 0
  };

  // Store last API response for debugging/recovery
  lastApiResponse: any = null;

  // Forms
  clientForm: FormGroup;
  inviteUserForm: FormGroup;
  endpointForm: FormGroup;
  secretsForm: FormGroup;

  // Data arrays
  clientEndpoints: ClientEndpointDto[] = [];
  clientUsers: ClientUserDto[] = [];
  clientCredentials: ClientCredentialsDto | null = null;
  private originalEndpointMappings: Map<string, string | null> = new Map();
  generatedCredentials: GenerateCredentialsResponse | null = null;
  
  // Auto-population state
  hasGeneratedCredentials = false;
  inboundCredentialsPopulated = false;
  
  // Computed property for OUTBOUND credential status
  get hasOutboundCredentials(): boolean {
    return !!(this.selectedClient?.hasOurSecretForClient || this.selectedClient?.ourAccountForClient);
  }
  
  // Computed property for OUTBOUND configuration status
  get hasOutboundConfiguration(): boolean {
    return !!(this.selectedClient?.apiBaseUrl && this.selectedClient?.clientAccountId);
  }
  
  // Endpoints for creation form
  newClientEndpoints: ClientEndpointDto[] = [];

  // Messages
  successMessage = '';
  errorMessage = '';

  // Card more-menu state (tracks which card's 3-dot menu is open)
  openMoreMenuId: string | null = null;

  // UI Options
  providerOptions: ProviderOption[] = [
    { value: ClientType.Ecom, label: 'Ecom', icon: 'lucideShoppingBag' },
    { value: ClientType.Sea, label: 'Sea', icon: 'lucideAnchor' },
    { value: ClientType.Air, label: 'Air', icon: 'lucidePlane' }
  ];

  statusOptions: StatusOption[] = [
    //{ value: ClientStatus.Draft, label: 'Draft', class: 'bg-secondary' },
    { value: ClientStatus.PendingVerification, label: 'Pending', class: 'bg-warning' },
    { value: ClientStatus.Active, label: 'Active', class: 'bg-success' },
    { value: ClientStatus.Inactive, label: 'Inactive', class: 'bg-danger' },
    { value: ClientStatus.Suspended, label: 'Suspended', class: 'bg-dark' }
  ];

  roleOptions: RoleOption[] = [
    { value: 'read', label: 'Read Only', description: 'View integration data' },
    { value: 'write', label: 'Read & Write', description: 'Manage integration data' },
    { value: 'admin', label: 'Administrator', description: 'Full access to integration' }
  ];

  endpointTypes: EndpointTypeOption[] = [
    { value: 'webhook', label: 'Webhook', description: 'Receive notifications from external service' },
    { value: 'api', label: 'API Endpoint', description: 'Make API calls to external service' },
    { value: 'callback', label: 'Callback URL', description: 'OAuth or authentication callback' }
  ];

  httpMethods: HttpMethodOption[] = [
    { value: 'GET', label: 'GET' },
    { value: 'POST', label: 'POST' },
    { value: 'PUT', label: 'PUT' },
    { value: 'PATCH', label: 'PATCH' },
    { value: 'DELETE', label: 'DELETE' }
  ];

  credentialDirections: CredentialDirection[] = [
    {
      value: 'INBOUND',
      label: 'Inbound Credentials',
      description: 'Credentials for receiving data from external systems',
      icon: 'lucideArrowDownCircle'
    },
    {
      value: 'OUTBOUND',
      label: 'Outbound Credentials',
      description: 'Credentials for sending data to external systems',
      icon: 'lucideArrowUpCircle'
    }
  ];

  credentialTypes: CredentialTypeOption[] = [
    {
      value: 'api_key',
      label: 'API Key Authentication',
      description: 'Simple API key for service authentication',
      icon: 'bi-key'
    },
    {
      value: 'jwt',
      label: 'JWT Authentication',
      description: 'JSON Web Token authentication with signed headers',
      icon: 'bi-shield-lock'
    },
    {
      value: 'oauth2',
      label: 'OAuth 2.0',
      description: 'OAuth 2.0 flow with client credentials',
      icon: 'bi-shield-check'
    },
    {
      value: 'basic_auth',
      label: 'Basic Authentication',
      description: 'Username and password authentication',
      icon: 'bi-person-lock'
    },
    {
      value: 'bearer_token',
      label: 'Bearer Token',
      description: 'Bearer token authentication',
      icon: 'bi-key-fill'
    },
    {
      value: 'custom',
      label: 'Custom Headers',
      description: 'Custom authentication headers',
      icon: 'bi-gear'
    }
  ];

  // DYNAMIC INTEGRATION: System feature options for endpoint mapping
  systemFeatureOptions: SystemFeatureOption[] = [
    {
      value: 'repack_create',
      label: 'Repack Create',
      description: 'Create new repack operations for orders',
      icon: 'lucidePlusCircle'
    },
    {
      value: 'repack_update',
      label: 'Repack Update',
      description: 'Update client about repack progress and completion',
      icon: 'lucidePackage'
    },
    {
      value: 'repack_cancel',
      label: 'Repack Cancel',
      description: 'Cancel existing repack operations',
      icon: 'lucideX'
    },
    {
      value: 'customs_clearance_progress',
      label: 'Customs Clearance Progress',
      description: 'Track and notify about customs clearance progress',
      icon: 'lucideShieldCheck'
    }
  ];

  constructor(
    private integrationService: IntegrationService,
    private clientIntegrationService: ClientIntegrationService,
    private fb: FormBuilder,
    private authService: AuthService,
    private cdr: ChangeDetectorRef
  ) {
    // Initialize forms in constructor
    // Overview form now only handles basic client info - credentials handled separately
    this.clientForm = this.fb.group({
      clientName: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(100)]],
      clientType: [ClientType.Ecom, Validators.required],
      description: ['', [Validators.required, Validators.minLength(10), Validators.maxLength(500)]]
    });

    this.inviteUserForm = this.fb.group({
      userId: ['', Validators.required],
      userName: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      role: ['read', Validators.required]
    });

    this.endpointForm = this.fb.group({
      endpointName: ['', [Validators.required, Validators.minLength(2)]],
      endpointPath: ['', [Validators.required, Validators.pattern(/^\/.*$/)]],
      httpMethod: ['POST', Validators.required],
      endpointType: ['api'],
      description: [''],
      isActive: [true],
      // DYNAMIC INTEGRATION: Add system feature mapping
      systemFeature: [undefined as any] // Optional - maps endpoint to system operation
    });

    this.secretsForm = this.fb.group({
      // Direction and type
      credentialDirection: ['OUTBOUND', Validators.required],
      credentialType: ['api_key', Validators.required],
      
      // Common fields
      clientId: [''],
      clientSecret: [''],
      apiKey: [''],
      
      // OAuth 2.0 fields
      accessToken: [''],
      refreshToken: [''],
      tokenEndpoint: [''],
      scope: [''],
      
      // Basic auth fields
      username: [''],
      password: [''],
      
      // Bearer token fields
      bearerToken: [''],
      
      // Custom header fields
      customHeaders: this.fb.array([]),
      
      // OUTBOUND specific fields - NEW comprehensive fields
      clientAccount: [''], // Client's account identifier
      clientBaseUrl: [''], // Client's API base URL
      authMethod: ['api_key'], // Authentication method for client API
      connectionTimeout: [30], // Connection timeout in seconds
      // REMOVED: targetEndpoint field - endpoints should be managed in Endpoints tab
      webhookSecret: [''],
      expirationDate: [''],
      description: [''],
      
      // Validation and testing
      testConnection: [true]
    });
  }

  ngOnInit(): void {
    // Verify authentication state before loading data
    const token = this.authService.getToken();
    if (!token) {
      console.error('Integration Management: No authentication token available');
      this.errorMessage = 'Authentication required. Please log in again.';
      return;
    }
    
    console.log('Integration Management: Token available, loading client integrations');
    this.loadClientIntegrations();
  }

  ngOnDestroy(): void {
    // RxJS timer will be automatically cleaned up by takeUntil(this.destroy$)
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Load all client integrations
   */
  loadClientIntegrations(): void {
    this.isLoading = true;
    
    this.integrationService.getClientIntegrations('', 1, 50)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.isLoading = false)
      )
      .subscribe({
        next: (clients: ClientIntegrationDto[]) => {
          this.integrationClients = clients;
        },
        error: (error: any) => {
          this.errorMessage = 'Failed to load integration clients';
          console.error('Load clients error:', error);
        }
      });
  }

  /**
   * Show create client form
   */
  showCreateClientForm(): void {
    this.showCreateClient = true;
    this.clientForm.reset({
      clientType: ClientType.Ecom
    });
  }

  /**
   * Show edit client form
   */
  showEditClientForm(client: ClientIntegrationDto): void {
    console.log('✏️ [showEditClientForm] Opening edit form for client:', client.id, client.clientName);
    
    this.selectedClient = client;
    this.showEditClient = true;
    this.activeTab = 'overview'; // Reset to overview tab
    
    // Clear previous client data
    this.clientCredentials = null;
    this.clientUsers = [];
    this.clientEndpoints = [];
    
    this.clientForm.patchValue({
      clientName: client.clientName,
      clientType: client.clientType,
      description: client.description
      // Remove credential fields - these are now handled in Credentials tab separately
    });
    
    // Load all client-related data
    console.log('🔍 [showEditClientForm] Loading client data for edit form');
    this.loadClientEndpoints(client.id);
    this.loadClientUsers(client.id);
    this.loadClientCredentials(client.id);
  }

  /**
   * Save client integration (create or update)
   */
  saveClient(): void {
    if (this.clientForm.invalid) {
      this.markFormGroupTouched(this.clientForm);
      console.warn('⚠️ [saveClient] Form validation failed:', {
        formErrors: Object.keys(this.clientForm.controls).reduce((errors, key) => {
          const control = this.clientForm.get(key);
          if (control && control.errors) {
            errors[key] = control.errors;
          }
          return errors;
        }, {} as any),
        formValues: this.clientForm.value
      });
      return;
    }

    this.isSaving = true;
    const formData = this.clientForm.value;
    
    // Pre-submission validation
    console.log('✅ [saveClient] Form validation passed. Form data:', formData);

    let apiCall;
    
    if (this.showEditClient) {
      // For update, prepare UpdateClientIntegrationDto - preserve existing credential values since they're handled separately
      const updateData = {
        clientName: formData.clientName,
        description: formData.description,
        // Preserve existing credential values since they're configured separately in Credentials tab
        apiBaseUrl: this.selectedClient!.apiBaseUrl || '',
        clientAccountId: this.selectedClient!.clientAccountId || '',
        endpoints: this.clientEndpoints.map(ep => ({
          id: ep.id || crypto.randomUUID(), // Ensure ID exists or generate new one
          endpointName: ep.endpointName,
          endpointPath: ep.endpointPath,
          httpMethod: ep.httpMethod,
          isActive: ep.isActive,
          // DYNAMIC INTEGRATION: Include system feature mapping
          systemFeature: ep.systemFeature
        }))
      };
      apiCall = this.integrationService.updateClientIntegration(this.selectedClient!.id, updateData);
    } else {
      // For create, prepare CreateClientIntegrationDto - use valid placeholder values for credentials
      const createData = {
        clientName: formData.clientName,
        clientType: formData.clientType,
        description: formData.description,
        // Use valid placeholder values - real credentials will be configured in Credentials tab after creation
        apiBaseUrl: 'https://api.placeholder.example.com/webhook',
        expectedAccountFromClient: 'TEMP_EXPECTED_ACCOUNT_FROM_CLIENT_ID',
        OurSecretForClient: 'temp-secret-20chars1',
        endpoints: this.newClientEndpoints,
        // CRITICAL FIX: Add required backend fields with proper values to prevent 400 error
        inboundSecret: 'temp-inbound-20chars2'
      };
      
      // DEBUG: Log the exact payload being sent to identify 400 error cause
      console.log('🔍 [saveClient] Creating client with payload:', JSON.stringify(createData, null, 2));
      apiCall = this.integrationService.createClientIntegration(createData);
    }

    apiCall
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.isSaving = false)
      )
      .subscribe({
        next: () => {
          if (this.showEditClient) {
            this.successMessage = 'Integration updated successfully';
          } else {
            this.successMessage = 'Integration created successfully! Configure credentials in the Credentials tab to complete setup.';
          }
          this.cancelClientForm();
          this.loadClientIntegrations();
        },
        error: (error: any) => {
          // Enhanced error logging for 400 debugging
          console.error('❌ [saveClient] API Error Details:', {
            status: error.status,
            statusText: error.statusText,
            message: error.message,
            url: error.url,
            error: error.error,
            fullError: error
          });
          
          // Extract detailed error message for user
          let errorMsg = this.showEditClient ? 'Failed to update integration' : 'Failed to create integration';
          
          if (error.status === 400) {
            if (error.error && typeof error.error === 'object') {
              // Try to extract validation errors
              if (error.error.errors) {
                const validationErrors = Object.keys(error.error.errors).map(key => 
                  `${key}: ${error.error.errors[key].join(', ')}`
                );
                errorMsg += `. Validation errors: ${validationErrors.join('; ')}`;
              } else if (error.error.message) {
                errorMsg += `. ${error.error.message}`;
              } else if (error.error.title) {
                errorMsg += `. ${error.error.title}`;
              }
            } else if (typeof error.error === 'string') {
              errorMsg += `. ${error.error}`;
            } else {
              errorMsg += '. Please check all required fields are valid.';
            }
          }
          
          this.errorMessage = errorMsg;
        }
      });
  }

  /**
   * Cancel client form
   */
  cancelClientForm(): void {
    this.showCreateClient = false;
    this.showEditClient = false;
    this.selectedClient = null;
    this.clientForm.reset();
    this.clientEndpoints = [];
    this.clientUsers = [];
    this.newClientEndpoints = []; // Reset new endpoints array
    this.clearSensitiveData(); // Clear any sensitive credential data
  }

  /**
   * Test connection to integration
   */
  testConnection(client: ClientIntegrationDto): void {
    this.isTesting = true;
    
    this.integrationService.testConnection(client.id)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.isTesting = false)
      )
      .subscribe({
        next: (result: any) => {
          if (result.success) {
            this.successMessage = `Connection to ${client.clientName} successful`;
          } else {
            this.errorMessage = `Connection test failed: ${result.message}`;
          }
        },
        error: (error: any) => {
          this.errorMessage = 'Connection test failed';
          console.error('Connection test error:', error);
        }
      });
  }

  /**
   * Show integration testing interface for client
   */
  showIntegrationTesting(client: ClientIntegrationDto): void {
    this.selectedClient = client;
    this.activeTab = 'testing';
  }

  /**
   * Handle tab change to load data as needed
   */
  onTabChange(tabName: string): void {
    this.activeTab = tabName;
    
    if (!this.selectedClient) return;
    
    console.log('🎯 [onTabChange] Switching to tab:', tabName, 'for client:', this.selectedClient.id);
    
    // Load data based on active tab
    switch (tabName) {
      case 'credentials':
        if (!this.clientCredentials) {
          console.log('🔍 [onTabChange] Loading credentials for credentials tab');
          this.loadClientCredentials(this.selectedClient.id);
        }
        break;
      case 'testing':
        // Ensure credentials are loaded for testing component
        console.log('🎯 [onTabChange] Switched to testing tab for client:', this.selectedClient.id);
        console.log('🔍 [onTabChange] Current client credentials status:', !!this.clientCredentials);
        
        // Always refresh credentials when switching to testing tab to ensure latest status
        console.log('🔄 [onTabChange] Refreshing credentials for testing tab to ensure latest status');
        this.loadClientCredentials(this.selectedClient.id);
        break;
      case 'audit':
        // Audit logs component will handle its own data loading
        console.log('🔍 [onTabChange] Switched to audit tab');
        break;
      case 'users':
        if (this.clientUsers.length === 0) {
          console.log('🔍 [onTabChange] Loading users for users tab');
          this.loadClientUsers(this.selectedClient.id);
        }
        break;
      case 'endpoints':
        if (this.clientEndpoints.length === 0 && this.selectedClient?.endpoints) {
          this.clientEndpoints = this.selectedClient.endpoints;
          console.log('🔍 [onTabChange] Loaded endpoints from selected client:', this.clientEndpoints.length);

          // Store original mappings for tracking changes
          this.originalEndpointMappings.clear();
          this.selectedClient.endpoints.forEach(ep => {
            this.originalEndpointMappings.set(ep.id, ep.systemFeature || null);
          });
        }
        break;
      case 'overview':
        console.log('🔍 [onTabChange] Switched to overview tab');
        break;
      default:
        console.log('🔍 [onTabChange] Unknown tab:', tabName);
        break;
    }
  }

  /**
   * Load client endpoints
   */
  loadClientEndpoints(clientId: string): void {
    // Endpoints are loaded as part of the client data
    if (this.selectedClient?.endpoints) {
      this.clientEndpoints = this.selectedClient.endpoints;

      // Store original mappings for tracking changes
      this.originalEndpointMappings.clear();
      this.selectedClient.endpoints.forEach(ep => {
        this.originalEndpointMappings.set(ep.id, ep.systemFeature || null);
      });
    }
  }

  /**
   * Load client users
   */
  loadClientUsers(clientId: string): void {
    this.integrationService.getClientUsers(clientId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (users) => {
          this.clientUsers = users;
        },
        error: (error) => {
          this.errorMessage = 'Failed to load users';
          console.error('Users error:', error);
        }
      });
  }

  /**
   * Show invite user modal
   */
  showInviteUserModal(client: ClientIntegrationDto): void {
    this.selectedClient = client;
    this.showInviteUser = true;
    this.inviteUserForm.reset({
      role: 'read'
    });
  }

  /**
   * Send user invitation
   */
  sendInvitation(): void {
    if (this.inviteUserForm.invalid) {
      this.markFormGroupTouched(this.inviteUserForm);
      return;
    }

    this.isSaving = true;
    const userData = {
      userId: this.inviteUserForm.value.userId,
      permissions: [this.inviteUserForm.value.role]
    };

    this.integrationService.linkUserToClient(this.selectedClient!.id, userData)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.isSaving = false)
      )
      .subscribe({
        next: () => {
          this.successMessage = 'User linked successfully';
          this.cancelInviteUser();
          this.loadClientUsers(this.selectedClient!.id);
        },
        error: (error: any) => {
          this.errorMessage = 'Failed to link user';
          console.error('Invite user error:', error);
        }
      });
  }

  /**
   * Cancel invite user modal
   */
  cancelInviteUser(): void {
    this.showInviteUser = false;
    this.inviteUserForm.reset();
  }

  /**
   * Show endpoint modal form
   */
  showEndpointModalForm(): void {
    this.showEndpointModal = true;
    this.endpointForm.reset({
      method: 'POST'
    });
  }

  /**
   * Save endpoint
   */
  saveEndpoint(): void {
    if (this.endpointForm.invalid) {
      this.markFormGroupTouched(this.endpointForm);
      return;
    }

    this.isSaving = true;
    const endpointData = this.endpointForm.value;

    if (this.showCreateClient) {
      // Add to the new client endpoints array for creation (no API call needed yet)
      this.newClientEndpoints.push({
        id: crypto.randomUUID(), // Generate unique ID for new endpoints
        endpointName: endpointData.endpointName,
        endpointPath: endpointData.endpointPath,
        httpMethod: endpointData.httpMethod,
        isActive: endpointData.isActive,
        // DYNAMIC INTEGRATION: Add system feature mapping
        systemFeature: (endpointData.systemFeature || undefined) as any
      });

      this.successMessage = 'Endpoint added successfully';
      this.cancelEndpointModal();
      this.isSaving = false;
    } else {
      // For existing clients, call the API to create the endpoint
      if (!this.selectedClient?.id) {
        this.errorMessage = 'No client selected';
        this.isSaving = false;
        return;
      }

      const newEndpoint = {
        id: crypto.randomUUID(), // Generate unique string ID
        endpointName: endpointData.endpointName,
        endpointPath: endpointData.endpointPath,
        httpMethod: endpointData.httpMethod,
        isActive: endpointData.isActive,
        // DYNAMIC INTEGRATION: Add system feature mapping
        systemFeature: (endpointData.systemFeature || undefined) as any
      };

      this.integrationService.createEndpoint(this.selectedClient.id, newEndpoint)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.successMessage = 'Endpoint created successfully';
            this.cancelEndpointModal();
            this.isSaving = false;
            // Refresh the endpoints list to show the new endpoint
            this.refreshClientEndpoints();
          },
          error: (error) => {
            console.error('Error creating endpoint:', error);
            this.errorMessage = 'Failed to create endpoint: ' + (error.error?.message || error.message);
            this.isSaving = false;
          }
        });
    }
  }

  /**
   * Remove endpoint from new client endpoints (create mode)
   */
  removeNewEndpoint(index: number): void {
    if (confirm('Are you sure you want to remove this endpoint?')) {
      this.newClientEndpoints.splice(index, 1);
      this.successMessage = 'Endpoint removed successfully';
    }
  }

  /**
   * Cancel endpoint modal
   */
  cancelEndpointModal(): void {
    this.showEndpointModal = false;
    this.endpointForm.reset();
  }

  /**
   * Remove linked user
   */
  removeLinkedUser(clientId: string, userId: number): void {
    if (!confirm('Are you sure you want to remove this user\'s access?')) {
      return;
    }

    this.integrationService.removeLinkedUser(clientId, userId.toString())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.successMessage = 'User removed successfully';
          this.loadClientUsers(clientId);
        },
        error: (error: any) => {
          this.errorMessage = 'Failed to remove user';
          console.error('Remove user error:', error);
        }
      });
  }

  /**
   * Delete client integration
   */
  deleteClient(client: ClientIntegrationDto): void {
    if (!confirm(`Are you sure you want to delete "${client.clientName}"? This action cannot be undone.`)) {
      return;
    }

    this.integrationService.deleteClientIntegration(client.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.successMessage = 'Integration deleted successfully';
          this.loadClientIntegrations();
        },
        error: (error: any) => {
          this.errorMessage = 'Failed to delete integration';
          console.error('Delete client error:', error);
        }
      });
  }

  /**
   * Show secrets modal with auto-population logic
   */
  showSecretsModalForm(client: ClientIntegrationDto): void {
    this.selectedClient = client;
    this.showSecretsModal = true;
    
    // Check for generated credentials to determine initial setup
    this.checkAndAutoPopulateCredentials(client.id).then(() => {
      // Default to OUTBOUND unless we have generated credentials and user wants INBOUND
      this.selectedCredentialDirection = 'OUTBOUND';
      this.selectedCredentialType = 'api_key';
      
      // Reset form without triggering subscriptions
      this.secretsForm.reset();
      
      // Set initial values programmatically
      this.secretsForm.patchValue({
        credentialDirection: 'OUTBOUND',
        credentialType: 'api_key',
        authMethod: 'api_key', // Keep in sync with credentialType
        testConnection: true
      }, { emitEvent: false });
      
      this.updateFormFieldsForCredentialType();
      
      // Subscribe to form changes with proper cleanup
      this.setupFormSubscriptions();
    });
  }

  /**
   * Setup form subscriptions with proper event handling
   */
  private setupFormSubscriptions(): void {
    // Clear any existing subscriptions for this form
    // Note: destroy$ will handle cleanup on component destroy
    console.log('📡 [setupFormSubscriptions] Setting up form change subscriptions');
    
    this.secretsForm.get('credentialDirection')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(direction => {
        console.log('📥 [setupFormSubscriptions] Direction value changed:', {
          newDirection: direction,
          currentDirection: this.selectedCredentialDirection
        });
        if (direction && direction !== this.selectedCredentialDirection) {
          console.log('🎯 [setupFormSubscriptions] Direction changed, updating');
          this.selectedCredentialDirection = direction;
          this.updateFormFieldsForCredentialType();
        }
      });
      
    this.secretsForm.get('credentialType')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(type => {
        console.log('🔄 [setupFormSubscriptions] Type value changed:', {
          newType: type,
          currentType: this.selectedCredentialType
        });
        if (type && type !== this.selectedCredentialType) {
          console.log('⚙️ [setupFormSubscriptions] Type changed, updating');
          this.selectedCredentialType = type;
          
          // Auto-sync authMethod with credentialType to avoid confusion
          this.secretsForm.patchValue({ 
            authMethod: type 
          }, { emitEvent: false });
          
          console.log('🔄 [setupFormSubscriptions] Auto-synced authMethod with credentialType:', type);
          this.updateFormFieldsForCredentialType();
        }
      });
      
    console.log('✅ [setupFormSubscriptions] Form subscriptions established');
  }

  /**
   * Handle credential direction change with auto-population
   */
  onCredentialDirectionChange(direction: 'INBOUND' | 'OUTBOUND'): void {
    console.log('🎯 [onCredentialDirectionChange] Direction change requested:', {
      from: this.selectedCredentialDirection,
      to: direction,
      hasGenerated: this.hasGeneratedCredentials
    });
    
    if (direction === this.selectedCredentialDirection) {
      console.log('🔄 [onCredentialDirectionChange] Direction unchanged, skipping');
      return; // Prevent unnecessary updates
    }
    
    this.selectedCredentialDirection = direction;
    
    // Update form without triggering change events
    this.secretsForm.patchValue({ 
      credentialDirection: direction 
    }, { emitEvent: false });
    
    // Auto-populate INBOUND credentials if available
    if (direction === 'INBOUND' && this.hasGeneratedCredentials && this.clientCredentials) {
      console.log('🤖 [onCredentialDirectionChange] Auto-populating INBOUND credentials');
      this.autoPopulateInboundCredentials();
    } else {
      console.log('⚙️ [onCredentialDirectionChange] Updating form fields for credential type');
      this.updateFormFieldsForCredentialType();
    }
    
    // Double-check API Key validation after direction change
    this.ensureApiKeyValidationIsCorrect(this.selectedCredentialType, direction);
  }

  /**
   * Handle credential type change
   */
  onCredentialTypeChange(type: string): void {
    console.log('🔄 [onCredentialTypeChange] Type change requested:', {
      from: this.selectedCredentialType,
      to: type,
      direction: this.selectedCredentialDirection
    });
    
    if (type === this.selectedCredentialType) {
      console.log('🔄 [onCredentialTypeChange] Type unchanged, skipping');
      return; // Prevent unnecessary updates
    }
    
    this.selectedCredentialType = type;
    
    // Update form without triggering change events
    this.secretsForm.patchValue({ 
      credentialType: type 
    }, { emitEvent: false });
    
    console.log('⚙️ [onCredentialTypeChange] Calling updateFormFieldsForCredentialType');
    this.updateFormFieldsForCredentialType();
    
    // Double-check API Key validation after type change
    this.ensureApiKeyValidationIsCorrect(type, this.selectedCredentialDirection);
  }

  /**
   * Update form field requirements based on credential type
   */
  updateFormFieldsForCredentialType(): void {
    const type = this.selectedCredentialType;
    const direction = this.selectedCredentialDirection;
    const isInboundWithGenerated = direction === 'INBOUND' && this.hasGeneratedCredentials;
    
    console.log('🔧 [updateFormFieldsForCredentialType] Starting validation update:', {
      type: type,
      direction: direction,
      isInboundWithGenerated: isInboundWithGenerated
    });
    
    // Reset all field validators
    Object.keys(this.secretsForm.controls).forEach(key => {
      if (!['credentialDirection', 'credentialType', 'testConnection'].includes(key)) {
        this.secretsForm.get(key)?.clearValidators();
        this.secretsForm.get(key)?.updateValueAndValidity();
        console.log(`🧹 [updateFormFieldsForCredentialType] Cleared validators for field: ${key}`);
      }
    });

    // Apply validators based on credential type (skip for auto-populated INBOUND)
    if (!isInboundWithGenerated) {
      console.log('⚙️ [updateFormFieldsForCredentialType] Applying validators (not auto-populated INBOUND)');
      
      // For OUTBOUND direction, always require the core OUTBOUND fields
      if (direction === 'OUTBOUND') {
        console.log('📤 [updateFormFieldsForCredentialType] Setting OUTBOUND validators');
        this.secretsForm.get('clientAccount')?.setValidators([Validators.required, Validators.minLength(2)]);
        this.secretsForm.get('clientSecret')?.setValidators([Validators.required, Validators.minLength(8)]);
        this.secretsForm.get('clientBaseUrl')?.setValidators([Validators.required, Validators.pattern(/^https?:\/\/.+/)]);
        this.secretsForm.get('connectionTimeout')?.setValidators([Validators.min(5), Validators.max(300)]);
      }
      
      // Apply additional validators based on credential type
      console.log(`🔍 [updateFormFieldsForCredentialType] Processing credential type: ${type}`);
      
      // CRITICAL FIX: Ensure API Key is NEVER required for JWT, OAuth2, Basic Auth, or Bearer Token
      const isApiKeyAuthType = type === 'api_key';
      const apiKeyControl = this.secretsForm.get('apiKey');
      
      if (isApiKeyAuthType) {
        console.log('🔑 [updateFormFieldsForCredentialType] API Key type - setting apiKey validator');
        apiKeyControl?.setValidators([Validators.required]);
        apiKeyControl?.updateValueAndValidity();
      } else {
        console.log(`🚫 [updateFormFieldsForCredentialType] Non-API-Key type (${type}) - CLEARING apiKey validator`);
        apiKeyControl?.clearValidators();
        apiKeyControl?.updateValueAndValidity();
      }
      
      switch (type) {
        case 'api_key':
          console.log('🔑 [updateFormFieldsForCredentialType] API Key case - apiKey validator already set above');
          // REMOVED: targetEndpoint validation - endpoints managed in Endpoints tab
          break;
        case 'jwt':
          console.log('🛡️ [updateFormFieldsForCredentialType] JWT case - apiKey validator already cleared above');

          if (direction === 'INBOUND') {
            console.log('📥 [updateFormFieldsForCredentialType] JWT INBOUND - setting clientId validator for editable account');
            // INBOUND JWT: API account should be required and editable
            this.secretsForm.get('clientId')?.setValidators([Validators.required, Validators.minLength(3)]);
            this.secretsForm.get('clientId')?.updateValueAndValidity();
          } else if (direction === 'OUTBOUND') {
            console.log('📤 [updateFormFieldsForCredentialType] JWT OUTBOUND - adding 20-char validation for clientSecret');
            // OUTBOUND JWT: Client Secret must be exactly 20 characters
            const clientSecretControl = this.secretsForm.get('clientSecret');
            clientSecretControl?.setValidators([
              Validators.required,
              Validators.minLength(20),
              Validators.maxLength(20)
            ]);
            clientSecretControl?.updateValueAndValidity();
          }
          console.log('🛡️ [updateFormFieldsForCredentialType] JWT processing complete');
          break;
        case 'oauth2':
          console.log('🔐 [updateFormFieldsForCredentialType] OAuth2 case - apiKey validator already cleared above');
          // OAuth2 specific validators
          this.secretsForm.get('clientId')?.setValidators([Validators.required]);
          this.secretsForm.get('clientSecret')?.setValidators([Validators.required]);
          if (direction === 'OUTBOUND') {
            this.secretsForm.get('tokenEndpoint')?.setValidators([Validators.pattern(/^https?:\/\/.+/)]);
          }
          break;
        case 'basic_auth':
          console.log('🔒 [updateFormFieldsForCredentialType] Basic Auth case - apiKey validator already cleared above');
          // Basic auth specific validators
          this.secretsForm.get('username')?.setValidators([Validators.required]);
          this.secretsForm.get('password')?.setValidators([Validators.required]);
          break;
        case 'bearer_token':
          console.log('🎫 [updateFormFieldsForCredentialType] Bearer Token case - apiKey validator already cleared above');
          // Bearer token specific validators
          this.secretsForm.get('bearerToken')?.setValidators([Validators.required]);
          break;
        default:
          console.warn(`⚠️ [updateFormFieldsForCredentialType] Unknown credential type: ${type}`);
          break;
      }
    } else {
      console.log('🤖 [updateFormFieldsForCredentialType] Skipping validation setup - auto-populated INBOUND');
    }
    
    // Update validity for all controls
    console.log('🔄 [updateFormFieldsForCredentialType] Updating validity for all controls');
    Object.keys(this.secretsForm.controls).forEach(key => {
      this.secretsForm.get(key)?.updateValueAndValidity();
    });
    
    // FINAL SAFETY CHECK: Ensure API Key validation is correct based on credential type
    this.ensureApiKeyValidationIsCorrect(type, direction);
    
    console.log('✅ [updateFormFieldsForCredentialType] Validation update completed');
  }

  /**
   * SAFETY METHOD: Ensure API Key validation is correct for the given credential type and direction
   */
  private ensureApiKeyValidationIsCorrect(credentialType: string, direction: 'INBOUND' | 'OUTBOUND'): void {
    const apiKeyControl = this.secretsForm.get('apiKey');
    if (!apiKeyControl) return;
    
    const shouldRequireApiKey = credentialType === 'api_key';
    const currentlyRequired = apiKeyControl.hasError('required') || 
                             (apiKeyControl.errors && apiKeyControl.errors['required'] !== undefined);
    
    console.log('🔍 [ensureApiKeyValidationIsCorrect] Checking API Key validation:', {
      credentialType: credentialType,
      direction: direction,
      shouldRequireApiKey: shouldRequireApiKey,
      currentlyRequired: currentlyRequired,
      hasErrors: !!apiKeyControl.errors,
      errors: apiKeyControl.errors,
      valid: apiKeyControl.valid
    });
    
    if (shouldRequireApiKey && !currentlyRequired) {
      console.log('⚠️ [ensureApiKeyValidationIsCorrect] API Key should be required but isn\'t - FIXING');
      apiKeyControl.setValidators([Validators.required]);
      apiKeyControl.updateValueAndValidity();
    } else if (!shouldRequireApiKey && currentlyRequired) {
      console.log('⚠️ [ensureApiKeyValidationIsCorrect] API Key shouldn\'t be required but is - FIXING');
      apiKeyControl.clearValidators();
      apiKeyControl.updateValueAndValidity();
    } else {
      console.log('✅ [ensureApiKeyValidationIsCorrect] API Key validation is correct');
    }
    
    // Final log of the state
    console.log('📊 [ensureApiKeyValidationIsCorrect] Final apiKey control state:', {
      hasValidators: !!apiKeyControl.validator,
      errors: apiKeyControl.errors,
      valid: apiKeyControl.valid,
      value: apiKeyControl.value,
      required: apiKeyControl.hasError('required')
    });
  }

  /**
   * Add custom header to the form array
   */
  addCustomHeader(): void {
    const customHeaders = this.secretsForm.get('customHeaders') as FormArray;
    const headerGroup = this.fb.group({
      name: ['', Validators.required],
      value: ['', Validators.required]
    });
    customHeaders.push(headerGroup);
  }

  /**
   * Remove custom header from the form array
   */
  removeCustomHeader(index: number): void {
    const customHeaders = this.secretsForm.get('customHeaders') as FormArray;
    customHeaders.removeAt(index);
  }

  /**
   * Get custom headers form array
   */
  getCustomHeaders(): FormArray<FormGroup> {
    return this.secretsForm.get('customHeaders') as FormArray<FormGroup>;
  }

  asFormGroup(c: AbstractControl): FormGroup {
    return c as FormGroup;
  }

  /**
   * Save client secrets
   */
  saveSecrets(): void {
    if (this.secretsForm.invalid) {
      this.markFormGroupTouched(this.secretsForm);
      return;
    }

    this.isSaving = true;
    const formValue = this.secretsForm.value;

    // Debug logging for JWT credentials
    console.log('🔐 [saveSecrets] JWT Debug:', {
      direction: formValue.credentialDirection,
      type: formValue.credentialType,
      clientSecret: formValue.clientSecret ? `[${formValue.clientSecret.length} chars]` : 'empty',
      apiKey: formValue.apiKey ? `[${formValue.apiKey.length} chars]` : 'empty',
      extractedSecret: this.getSecretKeyFromForm(formValue) ? `[${this.getSecretKeyFromForm(formValue).length} chars]` : 'empty',
      clientAccount: formValue.clientAccount
    });

    const secretsData: EnhancedClientSecretsDto = {
      credentialDirection: formValue.credentialDirection,
      credentialType: formValue.credentialType,
      secretKey: this.getSecretKeyFromForm(formValue),
      additionalData: this.getAdditionalCredentialData(formValue),
      testConnection: formValue.testConnection
    };

    // CRITICAL FIX: Pass credential existence info for proper POST/PUT selection
    // Check if outbound credentials already exist based on client data
    const hasExistingOutboundCredentials = formValue.credentialDirection === 'OUTBOUND' 
      ? (this.selectedClient?.hasOurSecretForClient === true || 
         this.selectedClient?.ourAccountForClient !== null && 
         this.selectedClient?.ourAccountForClient !== undefined && 
         this.selectedClient?.ourAccountForClient !== '')
      : undefined;
    
    console.log('🔍 [saveSecrets] Credential existence check:', {
      direction: formValue.credentialDirection,
      hasOurSecretForClient: this.selectedClient?.hasOurSecretForClient,
      ourAccountForClient: this.selectedClient?.ourAccountForClient,
      hasExistingOutboundCredentials: hasExistingOutboundCredentials
    });
    
    // Try enhanced secrets endpoint first, fallback to regular if not available
    const serviceCall = this.integrationService.updateEnhancedClientSecrets 
      ? this.integrationService.updateEnhancedClientSecrets(this.selectedClient!.id, secretsData, hasExistingOutboundCredentials)
      : this.integrationService.updateClientSecrets(this.selectedClient!.id, secretsData);
    
    serviceCall
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.isSaving = false)
      )
      .subscribe({
        next: () => {
          // If OUTBOUND credentials were updated, also update the client's basic fields
          if (formValue.credentialDirection === 'OUTBOUND' && this.selectedClient && formValue.clientBaseUrl && formValue.clientAccount) {
            this.updateClientBasicFields(formValue.clientBaseUrl, formValue.clientAccount);
          }
          
          // Show appropriate success message based on whether we created or updated
          const action = hasExistingOutboundCredentials === false ? 'created' : 'updated';
          this.successMessage = `${formValue.credentialDirection} credentials ${action} successfully`;
          this.cancelSecretsModal();
          
          // Refresh client data to show updated credential status
          if (this.selectedClient) {
            this.loadClientIntegrations(); // Reload to get updated client data
            // Also refresh credentials for testing component if on testing tab
            if (this.activeTab === 'testing') {
              setTimeout(() => {
                this.loadClientCredentials(this.selectedClient!.id);
              }, 500); // Small delay to ensure backend has processed the update
            }
          }
        },
        error: (error: any) => {
          this.errorMessage = 'Failed to update credentials';
          console.error('Update secrets error:', error);
        }
      });
  }

  /**
   * Update client's basic fields when OUTBOUND credentials are configured
   */
  private updateClientBasicFields(apiBaseUrl: string, clientAccountId: string): void {
    if (!this.selectedClient) return;
    
    const updateData = {
      clientName: this.selectedClient.clientName,
      description: this.selectedClient.description,
      apiBaseUrl: apiBaseUrl,
      clientAccountId: clientAccountId,
      endpoints: this.selectedClient.endpoints || []
    };

    this.integrationService.updateClientIntegration(this.selectedClient.id, updateData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          // Update the local client object
          this.selectedClient!.apiBaseUrl = apiBaseUrl;
          this.selectedClient!.clientAccountId = clientAccountId;
          console.log('✅ Client basic fields updated successfully');
        },
        error: (error) => {
          console.error('❌ Failed to update client basic fields:', error);
          // Don't show error to user as the credential update was successful
        }
      });
  }

  /**
   * Save credential changes without regenerating JWT secrets
   */
  saveCredentialChanges(): void {
    if (this.secretsForm.invalid) {
      this.markFormGroupTouched(this.secretsForm);
      return;
    }

    this.isSaving = true;
    const formValue = this.secretsForm.value;

    console.log('💾 [saveCredentialChanges] Saving credential changes:', {
      direction: formValue.credentialDirection,
      type: formValue.credentialType,
      clientAccount: formValue.clientAccount,
      baseUrl: formValue.clientBaseUrl
    });

    const secretsData: EnhancedClientSecretsDto = {
      credentialDirection: formValue.credentialDirection,
      credentialType: formValue.credentialType,
      secretKey: this.getSecretKeyFromForm(formValue),
      additionalData: this.getAdditionalCredentialData(formValue),
      testConnection: formValue.testConnection
    };

    // Check if outbound credentials already exist
    const hasExistingOutboundCredentials = formValue.credentialDirection === 'OUTBOUND'
      ? (this.selectedClient?.hasOurSecretForClient === true ||
         this.selectedClient?.ourAccountForClient !== null &&
         this.selectedClient?.ourAccountForClient !== undefined &&
         this.selectedClient?.ourAccountForClient !== '')
      : undefined;

    // Use enhanced secrets endpoint
    const serviceCall = this.integrationService.updateEnhancedClientSecrets
      ? this.integrationService.updateEnhancedClientSecrets(this.selectedClient!.id, secretsData, hasExistingOutboundCredentials)
      : this.integrationService.updateClientSecrets(this.selectedClient!.id, secretsData);

    serviceCall
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.isSaving = false)
      )
      .subscribe({
        next: (response: any) => {
          // Update local state with response data for INBOUND credentials
          if (formValue.credentialDirection === 'INBOUND' && response?.data) {
            const updatedApiAccount = response.data.UpdatedApiAccount || response.data.updatedApiAccount;

            // Update the API account in local clientCredentials
            if (this.clientCredentials && updatedApiAccount) {
              this.clientCredentials.apiAccount = updatedApiAccount;
            }

            // Update the expectedAccountFromClient in selectedClient
            if (this.selectedClient && updatedApiAccount) {
              this.selectedClient.expectedAccountFromClient = updatedApiAccount;
            }
          }

          // Update client basic fields for OUTBOUND credentials
          if (formValue.credentialDirection === 'OUTBOUND' && this.selectedClient && formValue.clientBaseUrl && formValue.clientAccount) {
            this.updateClientBasicFields(formValue.clientBaseUrl, formValue.clientAccount);
          }

          const action = hasExistingOutboundCredentials === false ? 'created' : 'updated';
          let successMsg = `${formValue.credentialDirection} credentials ${action} successfully`;

          // Add updated API account info to success message for INBOUND credentials
          if (formValue.credentialDirection === 'INBOUND' && response?.data) {
            const updatedApiAccount = response.data.UpdatedApiAccount || response.data.updatedApiAccount;
            if (updatedApiAccount) {
              successMsg += `. API account updated to: ${updatedApiAccount}`;
            }
          }

          this.successMessage = successMsg;
          this.cancelSecretsModal();

          // Refresh client data
          if (this.selectedClient) {
            this.loadClientIntegrations();
            if (this.activeTab === 'testing') {
              setTimeout(() => {
                this.loadClientCredentials(this.selectedClient!.id);
              }, 500);
            }
          }
        },
        error: (error: any) => {
          this.errorMessage = 'Failed to save credential changes. Please check if credential data is too large and try again.';
          console.error('Save credential changes error:', error);
        }
      });
  }

  /**
   * Regenerate JWT secret specifically for INBOUND JWT authentication
   */
  regenerateJwtSecret(): void {
    if (!this.selectedClient) return;

    if (!this.isJwtInboundSelected) {
      console.warn('Regenerate JWT secret called but not in JWT INBOUND mode');
      return;
    }

    if (!confirm('Are you sure you want to regenerate the JWT secret? This will invalidate the current secret and may disrupt active integrations.')) {
      return;
    }

    this.isSaving = true;

    this.clientIntegrationService.regenerateCredentials(this.selectedClient.id)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.isSaving = false)
      )
      .subscribe({
        next: (response) => {
          const responseAny = response as any;

          console.log('🔄 [regenerateJwtSecret] JWT Secret regenerated successfully');

          if ((responseAny?.retcode === 0 || response?.success) && response?.data) {
            const data = response.data as any;

            // Clear existing JWT state
            this.jwtSecretState = {
              secret: '',
              isVisible: false,
              countdown: 0
            };
            this.cdr.detectChanges();

            // Store generated credentials for display
            this.generatedCredentials = {
              apiAccount: data.apiAccount || data.ApiAccount || '',
              apiSecretKey: data.apiSecretKey || data.ApiSecretKey || '',
              allowedEndpoints: data.allowedEndpoints || data.AllowedEndpoints || [],
              usage: data.usage || data.Usage || 'Use these credentials to authenticate API requests',
              exampleJwtHeader: data.exampleJwtHeader || data.ExampleJwtHeader || {
                alg: 'HS256',
                typ: 'JWT'
              }
            };

            // Handle JWT secret display
            if (data.apiSecretKey) {
              console.log('🔑 [regenerateJwtSecret] Displaying JWT secret for 30 seconds');
              this.showJwtSecret(data.apiSecretKey);
            }

            this.successMessage = 'JWT secret regenerated successfully';

            // Refresh client data
            this.loadClientIntegrations();
          } else {
            this.errorMessage = 'Failed to regenerate JWT secret';
          }
        },
        error: (error: any) => {
          this.errorMessage = 'Failed to regenerate JWT secret';
          console.error('Regenerate JWT secret error:', error);
        }
      });
  }

  /**
   * Extract primary secret key based on credential type
   */
  private getSecretKeyFromForm(formValue: any): string {
    const direction = formValue.credentialDirection;

    switch (formValue.credentialType) {
      case 'api_key':
        // For API Key OUTBOUND, use clientSecret (per UI form design)
        // For API Key INBOUND, use apiKey (generated credentials)
        return direction === 'OUTBOUND' ? formValue.clientSecret : formValue.apiKey;
      case 'jwt':
        // For JWT OUTBOUND, use clientSecret (per UI message)
        // For JWT INBOUND, use apiKey (generated credentials)
        return direction === 'OUTBOUND' ? formValue.clientSecret : formValue.apiKey;
      case 'oauth2':
        return formValue.clientSecret;
      case 'basic_auth':
        return formValue.password;
      case 'bearer_token':
        return formValue.bearerToken;
      default:
        return formValue.apiKey || formValue.clientSecret || '';
    }
  }

  /**
   * Extract additional credential data
   */
  private getAdditionalCredentialData(formValue: any): any {
    const additionalData: any = {
      description: formValue.description,
      expirationDate: formValue.expirationDate
    };

    // Handle OUTBOUND-specific fields
    if (formValue.credentialDirection === 'OUTBOUND') {
      additionalData.clientAccount = formValue.clientAccount;
      additionalData.clientBaseUrl = formValue.clientBaseUrl;
      additionalData.authMethod = formValue.authMethod;
      additionalData.connectionTimeout = formValue.connectionTimeout;
      additionalData.webhookSecret = formValue.webhookSecret;
      // REMOVED: targetEndpoint - endpoints should be managed in Endpoints tab
    }

    // Handle INBOUND-specific fields for all credential types
    if (formValue.credentialDirection === 'INBOUND') {
      additionalData.clientId = formValue.clientId;
      additionalData.apiAccount = formValue.clientId; // API account for backend compatibility
    }

    // Handle credential type-specific fields
    switch (formValue.credentialType) {
      case 'api_key':
        // REMOVED: targetEndpoint - endpoints managed in Endpoints tab
        break;
      case 'jwt':
        // Include clientId (API account) for JWT credentials
        additionalData.clientId = formValue.clientId;
        additionalData.apiAccount = formValue.clientId; // Also include as apiAccount for backend compatibility
        break;
      case 'oauth2':
        additionalData.clientId = formValue.clientId;
        additionalData.tokenEndpoint = formValue.tokenEndpoint;
        additionalData.scope = formValue.scope;
        additionalData.accessToken = formValue.accessToken;
        additionalData.refreshToken = formValue.refreshToken;
        break;
      case 'basic_auth':
        additionalData.username = formValue.username;
        break;
      case 'custom':
        additionalData.customHeaders = formValue.customHeaders;
        break;
    }

    return additionalData;
  }

  /**
   * Test OUTBOUND connection with current credentials
   */
  testOutboundConnection(): void {
    if (!this.selectedClient) return;
    
    const formValue = this.secretsForm.value;
    this.isTesting = true;
    
    // Create test payload - test using configured endpoints from Endpoints tab
    const testData = {
      credentialType: formValue.credentialType,
      clientBaseUrl: formValue.clientBaseUrl,
      credentials: this.getAdditionalCredentialData(formValue)
    };

    // For now, simulate the test since the endpoint may not exist
    setTimeout(() => {
      this.isTesting = false;
      if (formValue.clientBaseUrl && this.getSecretKeyFromForm(formValue)) {
        this.successMessage = 'OUTBOUND connection test completed successfully. Use Endpoints tab to test specific endpoints.';
      } else {
        this.errorMessage = 'Please provide valid credentials and client base URL';
      }
    }, 2000);
  }

  /**
   * Handle secrets modal backdrop click
   */
  onSecretsModalBackdropClick(event: MouseEvent): void {
    // Close modal only if clicked on backdrop (not on modal content)
    if (event.target === event.currentTarget) {
      this.cancelSecretsModal();
    }
  }

  /**
   * Cancel secrets modal
   */
  cancelSecretsModal(): void {
    this.showSecretsModal = false;
    this.selectedCredentialDirection = 'OUTBOUND';
    this.selectedCredentialType = 'api_key';
    
    // Reset form without triggering events
    this.secretsForm.reset({}, { emitEvent: false });
    
    // Clear custom headers array
    const customHeaders = this.secretsForm.get('customHeaders') as FormArray;
    while (customHeaders.length !== 0) {
      customHeaders.removeAt(0);
    }
    
    // Clear any error states
    this.errorMessage = '';
  }

  /**
   * Show credentials modal
   */
  showCredentialsModalForm(client: ClientIntegrationDto): void {
    this.selectedClient = client;
    this.showCredentialsModal = true;
    this.loadClientCredentials(client.id);
  }

  /**
   * Show unified Authentication Management modal with integrated API credential functionality
   */
  showAuthenticationModalForm(client: ClientIntegrationDto): void {
    this.selectedClient = client;
    this.showSecretsModal = true;

    // Load existing credentials for both directions
    this.loadClientCredentials(client.id);

    // Check for generated credentials to determine initial setup
    this.checkAndAutoPopulateCredentials(client.id).then(() => {
      // If the client has no inbound credentials, start with INBOUND to encourage setup
      // Otherwise, default to OUTBOUND for configuration
      const hasInboundCredentials = client.hasInboundSecret || this.hasGeneratedCredentials;
      this.selectedCredentialDirection = hasInboundCredentials ? 'OUTBOUND' : 'INBOUND';
      this.selectedCredentialType = 'jwt'; // Default to JWT for better security

      console.log('🔐 [showAuthenticationModalForm] Modal initialized with:', {
        selectedCredentialDirection: this.selectedCredentialDirection,
        selectedCredentialType: this.selectedCredentialType,
        hasInboundCredentials: hasInboundCredentials,
        isJwtInboundSelected: this.isJwtInboundSelected
      });

      // Reset form without triggering subscriptions
      this.secretsForm.reset();

      // Set initial values programmatically
      this.secretsForm.patchValue({
        credentialDirection: this.selectedCredentialDirection,
        credentialType: this.selectedCredentialType,
        authMethod: this.selectedCredentialType,
        testConnection: true
      }, { emitEvent: false });

      this.updateFormFieldsForCredentialType();

      // Subscribe to form changes with proper cleanup
      this.setupFormSubscriptions();
    });
  }

  /**
   * Show Authentication Management modal focused on INBOUND JWT credentials
   */
  showInboundJwtAuthenticationModal(client: ClientIntegrationDto): void {
    this.selectedClient = client;
    this.showSecretsModal = true;

    // Explicitly set to INBOUND JWT for regeneration
    this.selectedCredentialDirection = 'INBOUND';
    this.selectedCredentialType = 'jwt';

    console.log('🔐 [showInboundJwtAuthenticationModal] Inbound JWT modal opened:', {
      selectedCredentialDirection: this.selectedCredentialDirection,
      selectedCredentialType: this.selectedCredentialType,
      isJwtInboundSelected: this.isJwtInboundSelected
    });

    // Load existing credentials
    this.loadClientCredentials(client.id);

    // Reset and setup form for INBOUND JWT
    this.secretsForm.reset();
    this.secretsForm.patchValue({
      credentialDirection: 'INBOUND',
      credentialType: 'jwt',
      authMethod: 'jwt',
      testConnection: true
    }, { emitEvent: false });

    this.updateFormFieldsForCredentialType();
    this.setupFormSubscriptions();
  }

  /**
   * Show Authentication Management modal focused on OUTBOUND credentials
   */
  showOutboundAuthenticationModal(client: ClientIntegrationDto): void {
    this.selectedClient = client;
    this.showSecretsModal = true;

    // Load existing credentials
    this.loadClientCredentials(client.id);

    // Check for generated credentials
    this.checkAndAutoPopulateCredentials(client.id).then(() => {
      // Always focus on OUTBOUND for this specific function
      this.selectedCredentialDirection = 'OUTBOUND';
      this.selectedCredentialType = 'api_key';

      // Reset form without triggering subscriptions
      this.secretsForm.reset();

      // Set initial values programmatically
      this.secretsForm.patchValue({
        credentialDirection: 'OUTBOUND',
        credentialType: 'api_key',
        authMethod: 'api_key',
        testConnection: true
      }, { emitEvent: false });

      this.updateFormFieldsForCredentialType();

      // Subscribe to form changes with proper cleanup
      this.setupFormSubscriptions();
    });
  }

  /**
   * Generate new API credentials
   */
  generateCredentials(): void {
    if (!this.selectedClient) return;

    this.isSaving = true;
    this.clientIntegrationService.generateCredentials(this.selectedClient.id)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.isSaving = false)
      )
      .subscribe({
        next: (response) => {
          if (response && response.success && response.data) {
            // Store the response for debugging/recovery
            this.lastApiResponse = response;
            console.log('📝 [regenerateCredentials] Stored API response for reference');

            // CRITICAL: Reset JWT state completely before processing new response
            console.log('🧹 [regenerateCredentials] Clearing all existing JWT state before processing new response');
            this.jwtSecretState = {
              secret: '',
              isVisible: false,
              countdown: 0
            };
            this.cdr.detectChanges();
            
            // Map the response data to GenerateCredentialsResponse format
            const data = response.data as any; // Cast to any to access additional properties
            // Handle both camelCase (new format) and PascalCase (legacy format) properties
            this.generatedCredentials = {
              apiAccount: data.apiAccount || data.ApiAccount || '',
              apiSecretKey: data.apiSecretKey || data.ApiSecretKey || '',
              allowedEndpoints: data.allowedEndpoints || data.AllowedEndpoints || [],
              usage: data.usage || data.Usage || 'Use these credentials to authenticate API requests',
              exampleJwtHeader: data.exampleJwtHeader || data.ExampleJwtHeader || {
                alg: 'HS256',
                typ: 'JWT',
                account: data.apiAccount || data.ApiAccount || ''
              }
            };
            this.showCredentialsInfo = true;
            this.successMessage = 'API credentials generated successfully';
            this.loadClientCredentials(this.selectedClient!.id);
            
            // Update auto-population flags
            this.hasGeneratedCredentials = true;
            
            // Trigger testing capabilities refresh if on testing tab
            if (this.activeTab === 'testing') {
              console.log('🔄 [generateCredentials] Triggering testing refresh after credential generation');
              setTimeout(() => {
                // The testing component should pick up the new credentials
                this.loadClientCredentials(this.selectedClient!.id);
              }, 1000);
            }
            
            // Auto-clear sensitive data after 5 minutes for security
            setTimeout(() => {
              this.clearSensitiveData();
            }, 300000); // 5 minutes
          } else {
            this.errorMessage = response?.message || 'Failed to generate credentials';
          }
        },
        error: (error: any) => {
          this.errorMessage = 'Failed to generate credentials';
          console.error('Generate credentials error:', error);
        }
      });
  }

  /**
   * Regenerate API credentials - SIMPLIFIED VERSION
   */
  regenerateCredentials(): void {
    if (!this.selectedClient) return;

    if (!confirm('Are you sure you want to regenerate credentials? This will invalidate the current credentials and may disrupt active integrations.')) {
      return;
    }

    this.isSaving = true;

    this.clientIntegrationService.regenerateCredentials(this.selectedClient.id)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.isSaving = false)
      )
      .subscribe({
        next: (response) => {
          // Cast to any to handle different response formats
          const responseAny = response as any;
          
          console.log('🔄 [regenerateCredentials] API Response received:', {
            format: responseAny?.retcode === 0 ? 'NEW (retcode)' : 'OLD (success)',
            success: responseAny?.retcode === 0 || response?.success
          });

          // Handle both response formats: { retcode: 0, data: ... } and { success: true, data: ... }
          if ((responseAny?.retcode === 0 || response?.success) && response?.data) {
            const data = response.data as any;

            console.log('📊 [regenerateCredentials] Processing API data with secret:', {
              hasApiSecret: !!data.apiSecretKey,
              secretLength: data.apiSecretKey?.length,
              authType: data.inboundCredentials?.authenticationType
            });

            // Store generated credentials
            this.generatedCredentials = {
              apiAccount: data.apiAccount || data.ApiAccount || '',
              apiSecretKey: data.apiSecretKey || data.ApiSecretKey || '',
              allowedEndpoints: data.allowedEndpoints || data.AllowedEndpoints || [],
              usage: data.usage || data.Usage || 'Use these credentials to authenticate API requests',
              exampleJwtHeader: data.exampleJwtHeader || data.ExampleJwtHeader || {
                alg: 'HS256',
                typ: 'JWT',
                account: data.apiAccount || data.ApiAccount || ''
              }
            };

            console.log('🔄 [regenerateCredentials] Generated Credentials Stored:', this.generatedCredentials);

            // Check for JWT secret and display it - Enhanced Detection
            const secret = data.apiSecretKey || data.ApiSecretKey;
            
            console.log('🔍 [regenerateCredentials] JWT Secret extracted:', {
              secretLength: secret?.length,
              isValidSecret: !!(secret && secret.length > 10),
              secretPreview: secret ? secret.substring(0, 20) + '...' : 'No secret found'
            });
            
            // Multiple detection methods for JWT INBOUND authentication
            const detectionResults = {
              hasSecret: !!(secret && secret.length > 10),
              method1_UIState: (this.selectedCredentialType === 'jwt' && this.selectedCredentialDirection === 'INBOUND'),
              method2_ResponseData: (data.inboundCredentials?.authenticationType === 'JWT'),
              method3_ModalAndSecret: (this.showSecretsModal && secret),
              method4_IntegrationSummary: (data.integrationSummary?.authenticationType === 'JWT' && 
                                         data.integrationSummary?.credentialDirection?.includes('INBOUND')),
              method5_HasJwtSecretAndInbound: (!!(secret && data.inboundCredentials && 
                                                data.inboundCredentials.authenticationType === 'JWT'))
            };

            const isJwtInbound = detectionResults.hasSecret && (
              detectionResults.method1_UIState ||
              detectionResults.method2_ResponseData ||
              detectionResults.method3_ModalAndSecret ||
              detectionResults.method4_IntegrationSummary ||
              detectionResults.method5_HasJwtSecretAndInbound
            );

            console.log('🔍 [regenerateCredentials] JWT Detection Analysis:', {
              secret: secret ? `Present (${secret.length} chars): ${secret.substring(0, 15)}...` : 'Missing',
              secretFull: secret, // Full secret for debugging
              selectedCredentialType: this.selectedCredentialType,
              selectedCredentialDirection: this.selectedCredentialDirection,
              responseAuthType: data.inboundCredentials?.authenticationType,
              integrationSummary: data.integrationSummary,
              showSecretsModal: this.showSecretsModal,
              
              // Detailed detection breakdown
              detectionResults: detectionResults,
              finalDecision: isJwtInbound
            });

            // CRITICAL: Clear any existing test secret state before processing real secret
            if (this.jwtSecretState.secret?.includes('TEST_SECRET')) {
              console.warn('⚠️ [regenerateCredentials] DETECTED TEST SECRET - Forcing clear before real regeneration');
              console.warn('Previous test secret:', this.jwtSecretState.secret);
              this.jwtSecretState = {
                secret: '',
                isVisible: false,
                countdown: 0
              };
              this.cdr.detectChanges();
            }

            // Always show JWT secret if we have a valid secret from API response
            if (secret && secret.length > 10) {
              console.log('✅ [regenerateCredentials] Displaying JWT secret for 30 seconds:', {
                secretLength: secret?.length,
                secretPreview: secret.substring(0, 15) + '...'
              });

              // FORCE CLEAR any existing state and show the real secret
              console.log('🔄 [regenerateCredentials] Force clearing JWT state and showing real secret');
              this.jwtSecretState = {
                secret: '',
                isVisible: false,
                countdown: 0
              };

              // Force change detection before setting new secret
              this.cdr.detectChanges();

              // Show the JWT secret for 30 seconds
              this.showJwtSecret(secret);
              
              // Verify secret was set correctly
              console.log('✅ [regenerateCredentials] JWT secret display initialized:', {
                isVisible: this.jwtSecretState.isVisible,
                countdown: this.jwtSecretState.countdown
              });
              
              this.successMessage = 'JWT secret regenerated successfully! Secret is now visible for 30 seconds - copy it immediately.';
            } else if (isJwtInbound) {
              console.log('✅ [regenerateCredentials] JWT INBOUND DETECTED (legacy path) - Processing secret display:', {
                secretToShow: secret,
                secretLength: secret?.length,
                secretPreview: secret ? secret.substring(0, 15) + '...' : 'Empty',
                isTestSecret: secret?.includes('TEST_SECRET') || false,
                currentJwtState: this.jwtSecretState
              });

              // FORCE CLEAR any existing state and show the real secret
              console.log('🔄 [regenerateCredentials] Force clearing JWT state and showing real secret');
              this.jwtSecretState = {
                secret: '',
                isVisible: false,
                countdown: 0
              };

              // Force change detection before setting new secret
              this.cdr.detectChanges();

              // Show the JWT secret for 30 seconds
              this.showJwtSecret(secret);
              this.successMessage = 'JWT secret regenerated successfully! Secret is now visible for 30 seconds - copy it immediately.';

              // VALIDATION: Verify the correct secret is now displayed
              setTimeout(() => {
                const currentDisplayedSecret = this.jwtSecretState.secret;
                const expectedSecret = secret;
                
                console.log('✅ [regenerateCredentials] Post-display validation:', {
                  expectedSecret: expectedSecret,
                  currentDisplayedSecret: currentDisplayedSecret,
                  secretsMatch: currentDisplayedSecret === expectedSecret,
                  isVisible: this.jwtSecretState.isVisible,
                  countdown: this.jwtSecretState.countdown
                });

                if (currentDisplayedSecret !== expectedSecret) {
                  console.error('❌ [regenerateCredentials] CRITICAL: Displayed secret does not match API response!');
                  console.error('Expected:', expectedSecret);
                  console.error('Displayed:', currentDisplayedSecret);
                  
                  // Auto-correct the mismatch
                  console.log('🔧 [regenerateCredentials] Auto-correcting secret mismatch');
                  this.jwtSecretState.secret = expectedSecret;
                  this.cdr.detectChanges();
                }
              }, 100);
            } else {
              console.log('ℹ️ [regenerateCredentials] Not JWT INBOUND, showing normal credentials');
              this.showCredentialsInfo = true;
              this.successMessage = 'Credentials regenerated successfully.';
            }

            // Update client status and reload credentials
            if (this.selectedClient && data.inboundCredentials) {
              this.selectedClient.hasInboundSecret = true;
              this.selectedClient.expectedAccountFromClient = data.inboundCredentials.account || data.apiAccount;
            }

            this.hasGeneratedCredentials = true;
            this.onCredentialsRegenerated();

            // IMPORTANT: Delay credential reload to avoid interfering with JWT secret display
            // Only reload if we're NOT showing JWT secrets, or delay it
            if (!isJwtInbound) {
              // Not JWT, safe to reload immediately
              this.loadClientCredentials(this.selectedClient?.id!);
            } else {
              // JWT secret is being displayed, delay reload to avoid interference
              console.log('🕐 [regenerateCredentials] Delaying credential reload to avoid JWT display interference');
              setTimeout(() => {
                console.log('🔄 [regenerateCredentials] Delayed credential reload executing');
                this.loadClientCredentials(this.selectedClient?.id!);
              }, 1000); // Delay 1 second to let JWT display stabilize
            }

            // Auto-clear sensitive data after 5 minutes
            setTimeout(() => {
              this.clearSensitiveData();
            }, 300000);

          } else {
            this.errorMessage = response?.message || 'Failed to regenerate credentials';
          }
        },
        error: () => {
          this.errorMessage = 'Failed to regenerate credentials';
        }
      });
  }

  // OLD COMPLEX METHODS REMOVED - REPLACED WITH SIMPLE RxJS IMPLEMENTATION

  /**
   * Check if JWT authentication is selected for INBOUND
   */
  get isJwtInboundSelected(): boolean {
    return this.selectedCredentialType === 'jwt' &&
           this.selectedCredentialDirection === 'INBOUND';
  }

  /**
   * Check if JWT secret field should be shown
   */
  shouldShowJwtSecretField(): boolean {
    return this.selectedCredentialType === 'jwt' &&
           this.selectedCredentialDirection === 'INBOUND';
  }







  /**
   * Show JWT secret for 30 seconds
   */
  showJwtSecret(secret: string): void {
    console.log('🔑 [showJwtSecret] Method called with secret:', {
      hasSecret: !!secret,
      secretLength: secret?.length || 0,
      secretPreview: secret ? secret.substring(0, 15) + '...' : 'Empty/Null'
    });
    
    if (!secret) {
      console.error('❌ [showJwtSecret] No secret provided - aborting');
      return;
    }
    
    console.log('✅ [showJwtSecret] Setting JWT state for 30-second display');
    
    // Set state directly
    this.jwtSecretState = {
      secret: secret,
      isVisible: true,
      countdown: 30
    };

    console.log('🔄 [showJwtSecret] JWT state set, forcing change detection');
    
    // Force change detection
    this.cdr.detectChanges();

    console.log('⏰ [showJwtSecret] Starting 30-second countdown timer');
    
    // Start countdown timer
    const timer = setInterval(() => {
      if (this.jwtSecretState.countdown > 0) {
        this.jwtSecretState.countdown--;
        this.cdr.detectChanges();
        
        if (this.jwtSecretState.countdown % 10 === 0) {
          console.log(`⏰ [showJwtSecret] Timer: ${this.jwtSecretState.countdown}s remaining`);
        }
      } else {
        console.log('🔒 [showJwtSecret] Timer expired - hiding secret');
        this.jwtSecretState.isVisible = false;
        this.jwtSecretState.secret = '';
        this.cdr.detectChanges();
        clearInterval(timer);
      }
    }, 1000);
    
    console.log('✅ [showJwtSecret] JWT secret display initialized successfully');
  }

  /**
   * Show current JWT secret from stored credentials
   */
  showCurrentJwtSecret(): void {
    console.log('👁️ [showCurrentJwtSecret] Eye button clicked - searching for JWT secret to show');
    
    // Try to get the secret from various sources
    let secret = '';
    let source = '';
    
    // First, try from last API response
    if (this.lastApiResponse?.data?.apiSecretKey) {
      secret = this.lastApiResponse.data.apiSecretKey;
      source = 'lastApiResponse.data.apiSecretKey';
    } else if (this.lastApiResponse?.data?.ApiSecretKey) {
      secret = this.lastApiResponse.data.ApiSecretKey;
      source = 'lastApiResponse.data.ApiSecretKey';
    }
    // Then try from generated credentials
    else if (this.generatedCredentials?.apiSecretKey) {
      secret = this.generatedCredentials.apiSecretKey;
      source = 'generatedCredentials.apiSecretKey';
    }

    console.log('🔍 [showCurrentJwtSecret] Secret search results:', {
      foundSecret: !!secret,
      secretLength: secret?.length || 0,
      secretPreview: secret ? secret.substring(0, 15) + '...' : 'None',
      source: source || 'No source found',
      lastApiResponse: !!this.lastApiResponse,
      generatedCredentials: !!this.generatedCredentials,
      lastApiResponseKeys: this.lastApiResponse ? Object.keys(this.lastApiResponse) : 'None',
      lastApiResponseDataKeys: this.lastApiResponse?.data ? Object.keys(this.lastApiResponse.data) : 'None'
    });

    if (secret) {
      console.log('🔑 [showCurrentJwtSecret] Showing stored JWT secret for 30 seconds from source:', source);
      this.showJwtSecret(secret);
    } else {
      console.warn('⚠️ [showCurrentJwtSecret] No JWT secret available to show');
      this.errorMessage = 'No JWT secret available. Please regenerate credentials first.';
      setTimeout(() => this.clearMessages(), 5000);
    }
  }

  /**
   * Check if there's a JWT secret available to show
   */
  hasJwtSecretToShow(): boolean {
    const hasSecret = !!(
      this.lastApiResponse?.data?.apiSecretKey ||
      this.lastApiResponse?.data?.ApiSecretKey ||
      this.generatedCredentials?.apiSecretKey
    );
    
    console.log('🔍 [hasJwtSecretToShow] Checking if JWT secret is available:', {
      hasSecret: hasSecret,
      lastApiResponseSecret: !!this.lastApiResponse?.data?.apiSecretKey,
      lastApiResponseSecretAlt: !!this.lastApiResponse?.data?.ApiSecretKey,
      generatedCredentialsSecret: !!this.generatedCredentials?.apiSecretKey,
      lastApiResponse: !!this.lastApiResponse,
      generatedCredentials: !!this.generatedCredentials
    });
    
    return hasSecret;
  }



  // OLD METHODS REMOVED - REPLACED WITH NEW SIMPLE GETTERS ABOVE

  // OLD DEBUG AND COPY METHODS REMOVED - REPLACED WITH NEW IMPLEMENTATION ABOVE

  /**
   * Load client credentials
   */
  loadClientCredentials(clientId: string): void {
    console.log('🔍 [loadClientCredentials] Starting credential loading for client:', clientId);
    
    // Preserve JWT secret state if it's currently visible
    const preserveJwtState = this.jwtSecretState.isVisible && this.jwtSecretState.secret;
    if (preserveJwtState) {
      console.log('🔒 [loadClientCredentials] Preserving active JWT secret state during credential reload');
    }
    
    // Validate clientId
    if (!clientId) {
      console.error('❌ [loadClientCredentials] Invalid clientId provided:', clientId);
      this.clientCredentials = null;
      this.errorMessage = 'Invalid client ID provided';
      return;
    }

    // Only show loading state if we're on the credentials tab
    if (this.activeTab === 'credentials') {
      this.isLoading = true;
      console.log('📋 [loadClientCredentials] Setting loading state for credentials tab');
    }

    const startTime = performance.now();
    console.log('⏱️ [loadClientCredentials] Starting API call at:', new Date().toISOString());

    this.clientIntegrationService.getCredentials(clientId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          const endTime = performance.now();
          const duration = Math.round(endTime - startTime);
          console.log(`⏱️ [loadClientCredentials] API call completed in ${duration}ms`);
          
          if (this.activeTab === 'credentials') {
            this.isLoading = false;
            console.log('📋 [loadClientCredentials] Clearing loading state for credentials tab');
          }
        })
      )
      .subscribe({
        next: (response) => {
          console.log('📡 [loadClientCredentials] Received API response:', response);
          
          try {
            // Handle different response formats
            let isSuccess = false;
            let responseData = null;
            
            // Check for different success indicators
            if (response && typeof response === 'object') {
              // Standard ApiResponse format
              if ('success' in response) {
                isSuccess = response.success === true;
                responseData = response.data;
                console.log('🔍 [loadClientCredentials] Standard ApiResponse format detected - success:', isSuccess);
              }
              // Alternative retcode format
              else if ('retcode' in response) {
                isSuccess = (response as any).retcode === 0;
                responseData = (response as any).data;
                console.log('🔍 [loadClientCredentials] Retcode format detected - retcode:', (response as any).retcode, 'success:', isSuccess);
              }
              // Direct data format (fallback)
              else if ('apiAccount' in response || 'allowedEndpoints' in response) {
                isSuccess = true;
                responseData = response;
                console.log('🔍 [loadClientCredentials] Direct data format detected');
              }
            }

            if (isSuccess && responseData) {
              console.log('✅ [loadClientCredentials] Processing successful response with data:', responseData);
              
              // Map the response data using the ClientCredentials interface from security.models
              const data = responseData as any;
              
              // Handle both camelCase (new format) and PascalCase (legacy format) properties
              this.clientCredentials = {
                apiAccount: this.getProperty(data, ['apiAccount', 'ApiAccount', 'account']),
                allowedEndpoints: this.getArrayProperty(data, ['allowedEndpoints', 'AllowedEndpoints', 'endpoints']),
                isActive: this.getBooleanProperty(data, ['isActive', 'IsActive', 'active']),
                createdAtUtc: this.getDateProperty(data, ['createdAtUtc', 'CreatedAtUtc', 'createdAt']),
                note: this.getProperty(data, ['note', 'Note', 'description']) || 'API credentials for client integration'
              };
              
              // Set auto-population flags when credentials are successfully loaded
              // Check for API account existence - allowedEndpoints might be empty but credentials still valid
              this.hasGeneratedCredentials = !!(this.clientCredentials.apiAccount);
              
              // Update selectedClient with inbound credential status from response
              if (this.selectedClient && data) {
                // Update inbound credential fields if they exist in the response
                if ('ExpectedAccountFromClient' in data) {
                  this.selectedClient.expectedAccountFromClient = data.ExpectedAccountFromClient;
                }
                if ('HasInboundSecret' in data) {
                  this.selectedClient.hasInboundSecret = data.HasInboundSecret;
                }
                // Also check for the inbound credentials from the credentialsSummary
                if (data.credentialsSummary?.hasInboundCredentials) {
                  this.selectedClient.hasInboundSecret = true;
                  if (!this.selectedClient.expectedAccountFromClient && this.clientCredentials.apiAccount) {
                    this.selectedClient.expectedAccountFromClient = this.clientCredentials.apiAccount;
                  }
                }
              }
              
              console.log('✅ [loadClientCredentials] Successfully mapped credentials:', {
                apiAccount: this.clientCredentials.apiAccount,
                allowedEndpointsCount: this.clientCredentials.allowedEndpoints?.length || 0,
                isActive: this.clientCredentials.isActive,
                createdAtUtc: this.clientCredentials.createdAtUtc,
                hasNote: !!this.clientCredentials.note,
                hasInboundSecret: this.selectedClient?.hasInboundSecret,
                expectedAccountFromClient: this.selectedClient?.expectedAccountFromClient
              });

              // IMPORTANT: Preserve JWT state if it was active before credential loading
              if (preserveJwtState) {
                console.log('🔒 [loadClientCredentials] JWT secret state preserved during credential reload:', {
                  jwtSecretState: this.jwtSecretState,
                  isStillVisible: this.jwtSecretState.isVisible,
                  hasSecret: !!this.jwtSecretState.secret
                });


              }
              
            } else if (isSuccess && !responseData) {
              // Success response but no credentials exist yet
              console.log('ℹ️ [loadClientCredentials] Successful response but no credential data - client has no credentials yet');
              this.clientCredentials = null;
            } else {
              console.log('⚠️ [loadClientCredentials] Response indicates failure or invalid format');
              console.log('Response details:', { isSuccess, responseData, fullResponse: response });
              this.clientCredentials = null;
            }
            
          } catch (processingError) {
            console.error('❌ [loadClientCredentials] Error processing response:', processingError);
            console.error('Response that caused error:', response);
            this.clientCredentials = null;
            this.errorMessage = 'Error processing credential data';
          }
        },
        error: (error) => {
          console.error('❌ [loadClientCredentials] API call failed:', error);
          console.error('Error details:', {
            status: error.status,
            statusText: error.statusText,
            message: error.message,
            url: error.url,
            error: error.error
          });
          
          // Clear credentials on any error
          this.clientCredentials = null;
          
          // Handle different error scenarios with detailed logging
          if (error.status === 404) {
            console.log('ℹ️ [loadClientCredentials] 404 response - no credentials found (normal for new clients)');
          } else if (error.status === 401) {
            console.error('🚫 [loadClientCredentials] 401 Unauthorized - authentication issue');
            this.errorMessage = 'Unauthorized access to client credentials. Please check your permissions.';
          } else if (error.status === 403) {
            console.error('🚫 [loadClientCredentials] 403 Forbidden - permission issue');
            this.errorMessage = 'Access forbidden. You do not have permission to view client credentials.';
          } else if (error.status >= 500) {
            console.error('🔥 [loadClientCredentials] Server error:', error.status, error.statusText);
            this.errorMessage = `Server error (${error.status}). Please try again later.`;
          } else if (error.status === 0) {
            console.error('🌐 [loadClientCredentials] Network error or CORS issue');
            this.errorMessage = 'Network error. Please check your connection.';
          } else {
            console.error('❓ [loadClientCredentials] Unexpected error:', error.status, error.statusText);
            this.errorMessage = `Error loading client credentials (${error.status})`;
          }
        }
      });
  }

  // Helper methods for property extraction with comprehensive logging
  private getProperty(obj: any, keys: string[]): string {
    for (const key of keys) {
      if (obj && obj[key] !== undefined && obj[key] !== null) {
        console.log(`🔍 [getProperty] Found property '${key}':`, obj[key]);
        return String(obj[key]);
      }
    }
    console.log(`⚠️ [getProperty] Property not found in keys:`, keys, 'from object:', obj);
    return '';
  }

  private getArrayProperty(obj: any, keys: string[]): string[] {
    for (const key of keys) {
      if (obj && Array.isArray(obj[key])) {
        console.log(`🔍 [getArrayProperty] Found array property '${key}':`, obj[key]);
        return obj[key];
      }
      if (obj && obj[key] && typeof obj[key] === 'string') {
        try {
          const parsed = JSON.parse(obj[key]);
          if (Array.isArray(parsed)) {
            console.log(`🔍 [getArrayProperty] Found stringified array property '${key}':`, parsed);
            return parsed;
          }
        } catch (e) {
          // Not JSON, continue
        }
      }
    }
    console.log(`⚠️ [getArrayProperty] Array property not found in keys:`, keys, 'from object:', obj);
    return [];
  }

  private getBooleanProperty(obj: any, keys: string[]): boolean {
    for (const key of keys) {
      if (obj && obj[key] !== undefined && obj[key] !== null) {
        const value = obj[key];
        let boolValue: boolean;
        
        if (typeof value === 'boolean') {
          boolValue = value;
        } else if (typeof value === 'string') {
          boolValue = value.toLowerCase() === 'true';
        } else if (typeof value === 'number') {
          boolValue = value !== 0;
        } else {
          boolValue = Boolean(value);
        }
        
        console.log(`🔍 [getBooleanProperty] Found boolean property '${key}':`, value, '→', boolValue);
        return boolValue;
      }
    }
    console.log(`⚠️ [getBooleanProperty] Boolean property not found in keys:`, keys, 'from object:', obj);
    return false;
  }

  private getDateProperty(obj: any, keys: string[]): Date {
    for (const key of keys) {
      if (obj && obj[key] !== undefined && obj[key] !== null) {
        const value = obj[key];
        let dateValue: Date;
        
        if (value instanceof Date) {
          dateValue = value;
        } else if (typeof value === 'string' || typeof value === 'number') {
          dateValue = new Date(value);
          
          // Validate the date
          if (isNaN(dateValue.getTime())) {
            console.warn(`⚠️ [getDateProperty] Invalid date value '${key}':`, value);
            continue;
          }
        } else {
          console.warn(`⚠️ [getDateProperty] Unexpected date type for '${key}':`, typeof value, value);
          continue;
        }
        
        console.log(`🔍 [getDateProperty] Found date property '${key}':`, value, '→', dateValue);
        return dateValue;
      }
    }
    console.log(`⚠️ [getDateProperty] Date property not found in keys:`, keys, 'from object:', obj);
    return new Date(); // Default to current date
  }

  /**
   * Cancel credentials modal
   */
  cancelCredentialsModal(): void {
    this.showCredentialsModal = false;
    this.showCredentialsInfo = false;
    this.clientCredentials = null;
    this.generatedCredentials = null;
  }

  // =========================
  // NEW SIMPLIFIED JWT METHODS
  // =========================

  /**
   * Get JWT secret display value
   */
  get jwtSecretDisplayValue(): string {
    const shouldShowSecret = this.jwtSecretState.isVisible && this.jwtSecretState.secret;
    const displayValue = shouldShowSecret
      ? this.jwtSecretState.secret
      : '••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••';
    
    // Only log when secret visibility changes for debugging
    if (shouldShowSecret && this.jwtSecretState.countdown === 30) {
      console.log('🔑 [jwtSecretDisplayValue] JWT secret now visible for 30 seconds');
    }
    
    return displayValue;
  }

  /**
   * Check if JWT secret can be copied
   */
  get canCopyJwtSecret(): boolean {
    return this.jwtSecretState.isVisible && !!this.jwtSecretState.secret;
  }

  /**
   * Copy JWT secret to clipboard
   */
  copyJwtSecretToClipboard(): void {
    if (this.canCopyJwtSecret) {
      navigator.clipboard.writeText(this.jwtSecretState.secret)
        .then(() => {
          this.successMessage = 'JWT secret copied to clipboard';
          // Auto-clear clipboard after 30 seconds for security
          setTimeout(() => {
            navigator.clipboard.writeText('').catch(() => {
              // Ignore errors when clearing clipboard
            });
          }, 30000);
        })
        .catch(() => {
          this.errorMessage = 'Failed to copy JWT secret';
        });
    } else {
      this.errorMessage = 'Secret not available. Please regenerate credentials.';
    }
  }

  /**
   * Show JWT secret for 30 seconds - simplified version
   */
  private showJwtSecretFor30Seconds(secret: string): void {
    this.showJwtSecret(secret);
  }

  /**
   * Copy to clipboard
   */
  copyToClipboard(text: string, type: string): void {
    navigator.clipboard.writeText(text).then(() => {
      this.successMessage = `${type} copied to clipboard`;
      // Auto-clear clipboard after 30 seconds for security
      setTimeout(() => {
        navigator.clipboard.writeText('').catch(() => {
          // Ignore errors when clearing clipboard
        });
      }, 30000);
    }).catch(() => {
      this.errorMessage = `Failed to copy ${type}`;
    });
  }

  /**
   * Mask secret key for display
   */
  maskSecret(secret: string): string {
    if (!secret || secret.length < 8) {
      return '****';
    }
    return secret.substring(0, 4) + '*'.repeat(secret.length - 8) + secret.substring(secret.length - 4);
  }

  /**
   * Clear sensitive data from memory
   */
  clearSensitiveData(): void {
    this.generatedCredentials = null;
    this.showCredentialsInfo = false;
    this.hasGeneratedCredentials = false;
    this.inboundCredentialsPopulated = false;
  }

  /**
   * Check for generated credentials and set auto-population flag
   */
  async checkAndAutoPopulateCredentials(clientId: string): Promise<void> {
    try {
      this.hasGeneratedCredentials = false;
      this.inboundCredentialsPopulated = false;
      
      if (this.clientCredentials) {
        // Already loaded credentials, check if they exist - apiAccount is sufficient
        this.hasGeneratedCredentials = !!(this.clientCredentials.apiAccount);
        console.log('🔍 [checkAndAutoPopulateCredentials] Using cached credentials - hasGenerated:', this.hasGeneratedCredentials);
      } else {
        // Load credentials if not already loaded
        console.log('🔍 [checkAndAutoPopulateCredentials] Loading credentials for auto-population check');
        
        return new Promise((resolve) => {
          this.clientIntegrationService.getCredentials(clientId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (response) => {
                if (response?.success && response.data) {
                  // Check for apiAccount existence - that's sufficient to indicate generated credentials
                  this.hasGeneratedCredentials = !!(response.data.apiAccount);
                  console.log('✅ [checkAndAutoPopulateCredentials] Credentials loaded - hasGenerated:', this.hasGeneratedCredentials);
                } else {
                  this.hasGeneratedCredentials = false;
                  console.log('ℹ️ [checkAndAutoPopulateCredentials] No generated credentials found');
                }
                resolve();
              },
              error: (error) => {
                console.error('❌ [checkAndAutoPopulateCredentials] Error loading credentials:', error);
                this.hasGeneratedCredentials = false;
                resolve();
              }
            });
        });
      }
    } catch (error) {
      console.error('❌ [checkAndAutoPopulateCredentials] Unexpected error:', error);
      this.hasGeneratedCredentials = false;
      this.inboundCredentialsPopulated = false;
    }
  }

  /**
   * Auto-populate INBOUND credentials from generated API credentials
   */
  autoPopulateInboundCredentials(): void {
    if (!this.hasGeneratedCredentials || !this.clientCredentials) {
      console.warn('⚠️ [autoPopulateInboundCredentials] Cannot auto-populate - no generated credentials available');
      return;
    }

    console.log('🔄 [autoPopulateInboundCredentials] Auto-populating INBOUND credentials');
    
    // Set credential type to JWT for generated credentials
    this.selectedCredentialType = 'jwt';
    
    // Populate form with generated credential data
    this.secretsForm.patchValue({
      credentialDirection: 'INBOUND',
      credentialType: 'jwt',
      clientId: this.clientCredentials.apiAccount,
      apiKey: '***', // Mask the actual secret for security
      description: 'Auto-populated from generated API credentials',
      testConnection: true
    }, { emitEvent: false });

    // Mark as populated
    this.inboundCredentialsPopulated = true;
    
    // Update form field requirements
    this.updateFormFieldsForCredentialType();
    
    // Disable form fields for auto-populated credentials
    this.setInboundFormReadOnlyState(true);

    console.log('✅ [autoPopulateInboundCredentials] INBOUND credentials auto-populated successfully');
  }

  /**
   * Set read-only state for INBOUND form fields when auto-populated
   * Note: clientId (API Account) remains editable as per UI requirements
   */
  setInboundFormReadOnlyState(isReadOnly: boolean): void {
    // Fields that should be read-only when auto-populated
    // Exclude 'clientId' to keep API Account field editable
    const fieldsToControl = ['credentialType', 'apiKey', 'description'];

    fieldsToControl.forEach(fieldName => {
      const control = this.secretsForm.get(fieldName);
      if (control) {
        if (isReadOnly) {
          control.disable();
        } else {
          control.enable();
        }
      }
    });

    // Ensure clientId (API Account) always remains enabled for editing
    const clientIdControl = this.secretsForm.get('clientId');
    if (clientIdControl) {
      clientIdControl.enable();
    }
  }

  /**
   * Check if current form represents auto-populated INBOUND credentials
   */
  isAutoPopulatedInboundCredentials(): boolean {
    return this.selectedCredentialDirection === 'INBOUND' && 
           this.hasGeneratedCredentials && 
           this.inboundCredentialsPopulated;
  }

  /**
   * Get credential source label for display
   */
  getCredentialSourceLabel(): string {
    if (this.isAutoPopulatedInboundCredentials()) {
      return 'Auto-generated';
    }
    return 'Manual';
  }

  /**
   * Get credential authentication type display
   */
  getCredentialAuthTypeDisplay(): string {
    if (this.isAutoPopulatedInboundCredentials()) {
      return 'JWT (Generated)';
    }
    
    const selectedType = this.credentialTypes.find(t => t.value === this.selectedCredentialType);
    return selectedType ? selectedType.label : 'Unknown';
  }

  /**
   * Handle regeneration of credentials (should update INBOUND auto-populated data)
   */
  onCredentialsRegenerated(): void {
    if (this.selectedCredentialDirection === 'INBOUND' && this.inboundCredentialsPopulated) {
      console.log('🔄 [onCredentialsRegenerated] Updating auto-populated INBOUND credentials after regeneration');
      
      // Reload client credentials
      if (this.selectedClient) {
        this.loadClientCredentials(this.selectedClient.id);
      }
      
      // Re-populate the form
      setTimeout(() => {
        this.autoPopulateInboundCredentials();
      }, 1000);
    }
  }

  /**
   * Delete endpoint
   */
  deleteEndpoint(endpointId: string): void {
    if (!confirm('Are you sure you want to delete this endpoint?')) {
      return;
    }

    // If we're in create client mode, just remove from local array
    if (this.showCreateClient) {
      this.newClientEndpoints = this.newClientEndpoints.filter(ep => ep.id !== endpointId);
      this.successMessage = 'Endpoint removed successfully';
      return;
    }

    // For existing clients, call the API to delete the endpoint
    if (!this.selectedClient?.id) {
      this.errorMessage = 'No client selected';
      return;
    }

    this.isSaving = true;
    this.integrationService.deleteEndpoint(this.selectedClient.id, endpointId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          // Remove from local array only after successful backend deletion
          this.clientEndpoints = this.clientEndpoints.filter(ep => ep.id !== endpointId);
          this.successMessage = 'Endpoint deleted successfully';
          this.isSaving = false;
          // Refresh the endpoints list to ensure synchronization
          this.refreshClientEndpoints();
        },
        error: (error) => {
          console.error('Error deleting endpoint:', error);
          this.errorMessage = 'Failed to delete endpoint: ' + (error.error?.message || error.message);
          this.isSaving = false;
        }
      });
  }

  /**
   * Clear messages
   */
  clearMessages(): void {
    this.successMessage = '';
    this.errorMessage = '';
  }

  /**
   * Get provider icon class
   */
  getProviderIcon(clientType: ClientType): string {
    const provider = this.providerOptions.find(p => p.value === clientType);
    return provider ? provider.icon : 'lucideCircleHelp';
  }

  /**
   * Get client type label
   */
  getClientTypeLabel(clientType: ClientType): string {
    const provider = this.providerOptions.find(p => p.value === clientType);
    return provider ? provider.label : 'Unknown';
  }

  /**
   * Get status badge class
   */
  getStatusBadgeClass(status: ClientStatus): string {
    const statusOption = this.statusOptions.find(s => s.value === status);
    return statusOption ? statusOption.class : 'bg-secondary';
  }

  /**
   * Get status label
   */
  getStatusLabel(status: ClientStatus): string {
    const statusOption = this.statusOptions.find(s => s.value === status);
    return statusOption ? statusOption.label : 'Unknown';
  }

  /**
   * Get selected role description
   */
  getSelectedRoleDescription(): string {
    const selectedRole = this.inviteUserForm.get('role')?.value;
    const roleOption = this.roleOptions.find(r => r.value === selectedRole);
    return roleOption ? roleOption.description : '';
  }

  /**
   * Get selected endpoint type description
   */
  getSelectedEndpointTypeDescription(): string {
    const selectedType = this.endpointForm.get('endpointType')?.value;
    const typeOption = this.endpointTypes.find(t => t.value === selectedType);
    return typeOption ? typeOption.description : '';
  }

  /**
   * Check if form field has error
   */
  hasFieldError(form: FormGroup, fieldName: string): boolean {
    const field = form.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  /**
   * Get field error message
   */
  getFieldError(form: FormGroup, fieldName: string): string {
    const field = form.get(fieldName);
    if (field && field.errors) {
      if (field.errors['required']) return `${fieldName} is required`;
      if (field.errors['email']) return 'Invalid email format';
      if (field.errors['minlength']) return `${fieldName} is too short`;
      if (field.errors['pattern']) return `${fieldName} format is invalid`;
    }
    return '';
  }

  /**
   * Mark all form fields as touched
   */
  markFormGroupTouched(form: FormGroup): void {
    Object.keys(form.controls).forEach(key => {
      const control = form.get(key);
      control?.markAsTouched();
    });
  }

  /**
   * Get client type description
   */
  getClientTypeDescription(clientType: ClientType): string {
    switch (clientType) {
      case ClientType.Ecom: return 'E-commerce platform integration';
      case ClientType.Sea: return 'Sea freight logistics';
      case ClientType.Air: return 'Air freight logistics';
      default: return 'Unknown integration type';
    }
  }

  /**
   * Generate random secret key
   */
  generateRandomSecret(): void {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < 64; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    this.clientForm.patchValue({ secretKey: result });
  }

  /**
   * Get endpoint count by type
   */
  getEndpointCountByType(type: string): number {
    return this.clientEndpoints.filter(ep => {
      const path = ep.endpointPath.toLowerCase();
      switch (type) {
        case 'webhook': return path.includes('webhook') || path.includes('notify');
        case 'api': return path.includes('api') && !path.includes('webhook');
        case 'callback': return path.includes('callback') || path.includes('auth');
        default: return false;
      }
    }).length;
  }

  /**
   * Get active endpoint count
   */
  getActiveEndpointCount(): number {
    return this.clientEndpoints.filter(ep => ep.isActive).length;
  }

  // ===== UX IMPROVEMENT: Endpoint Mapping State Management =====

  /**
   * Track which endpoints are in edit mode
   */
  private editingEndpointIds = new Set<string>();

  /**
   * Check if an endpoint is already mapped
   */
  isEndpointMapped(endpoint: ClientEndpointDto): boolean {
    return !!endpoint.systemFeature;
  }

  /**
   * Check if dropdown should be disabled (mapped and not editing)
   */
  shouldDisableDropdown(endpoint: ClientEndpointDto): boolean {
    return this.isEndpointMapped(endpoint) && !this.isEditingEndpoint(endpoint.id);
  }

  /**
   * Start editing an endpoint mapping
   */
  startEditingEndpoint(endpointId: string): void {
    this.editingEndpointIds.add(endpointId);
    console.log('🔓 [startEditingEndpoint] Enabled editing for endpoint:', endpointId);
  }

  /**
   * Stop editing an endpoint mapping
   */
  stopEditingEndpoint(endpointId: string): void {
    this.editingEndpointIds.delete(endpointId);
    console.log('🔒 [stopEditingEndpoint] Disabled editing for endpoint:', endpointId);
  }

  /**
   * Check if endpoint is in edit mode
   */
  isEditingEndpoint(endpointId: string): boolean {
    return this.editingEndpointIds.has(endpointId);
  }

  /**
   * Get row class based on mapping status
   */
  getEndpointRowClass(endpoint: ClientEndpointDto): string {
    if (this.isEditingEndpoint(endpoint.id)) {
      return 'endpoint-row-editing';
    }
    return this.isEndpointMapped(endpoint) ? 'endpoint-row-mapped' : 'endpoint-row-unmapped';
  }

  /**
   * Get mapping status badge class
   */
  getMappingStatusBadgeClass(endpoint: ClientEndpointDto): string {
    if (this.isEndpointMapped(endpoint)) {
      return 'bg-success';
    }
    return 'bg-warning';
  }

  /**
   * Get mapping status label
   */
  getMappingStatusLabel(endpoint: ClientEndpointDto): string {
    if (this.isEndpointMapped(endpoint)) {
      return 'Mapped';
    }
    return 'Not Mapped';
  }

  /**
   * Cancel endpoint editing without saving
   */
  cancelEndpointEdit(endpoint: ClientEndpointDto): void {
    // Restore original value if cancelled
    const originalFeature = this.originalEndpointMappings.get(endpoint.id);
    endpoint.systemFeature = (originalFeature || undefined) as any;
    this.stopEditingEndpoint(endpoint.id);
  }

  // ===== End of UX Improvement Methods =====

  /**
   * Get endpoint icon based on HTTP method
   */
  getEndpointIcon(method: string): string {
    switch (method.toUpperCase()) {
      case 'GET': return 'bi-download';
      case 'POST': return 'bi-upload';
      case 'PUT': return 'bi-arrow-up';
      case 'PATCH': return 'bi-pencil';
      case 'DELETE': return 'bi-trash';
      default: return 'bi-link';
    }
  }

  /**
   * Get method badge class
   */
  getMethodBadgeClass(method: string): string {
    switch (method.toUpperCase()) {
      case 'GET': return 'bg-info';
      case 'POST': return 'bg-success';
      case 'PUT': return 'bg-warning';
      case 'PATCH': return 'bg-secondary';
      case 'DELETE': return 'bg-danger';
      default: return 'bg-primary';
    }
  }

  /**
   * Get endpoint type from path
   */
  getEndpointTypeFromPath(path: string): string {
    const lowerPath = path.toLowerCase();
    if (lowerPath.includes('webhook') || lowerPath.includes('notify')) return 'Webhook';
    if (lowerPath.includes('callback') || lowerPath.includes('auth')) return 'Callback';
    if (lowerPath.includes('api')) return 'API';
    return 'Custom';
  }

  /**
   * Track function for endpoint list
   */
  trackEndpointById(index: number, endpoint: ClientEndpointDto): string {
    return endpoint.id;
  }

  /**
   * Toggle endpoint status
   */
  toggleEndpointStatus(endpoint: ClientEndpointDto): void {
    endpoint.isActive = !endpoint.isActive;
    this.successMessage = `Endpoint ${endpoint.endpointName} ${endpoint.isActive ? 'activated' : 'deactivated'}`;
    // Auto-save the changes
    if (this.selectedClient) {
      this.saveClient();
    }
  }

  /**
   * Test specific endpoint
   */
  testEndpoint(endpoint: ClientEndpointDto): void {
    this.isTesting = true;
    console.log('Testing endpoint:', endpoint);
    // Mock endpoint test - replace with actual implementation
    setTimeout(() => {
      this.isTesting = false;
      this.successMessage = `Endpoint ${endpoint.endpointName} test completed`;
    }, 2000);
  }

  /**
   * Test all endpoints
   */
  testAllEndpoints(): void {
    this.isTesting = true;
    const activeEndpoints = this.clientEndpoints.filter(ep => ep.isActive);
    console.log('Testing all active endpoints:', activeEndpoints);
    // Mock bulk test - replace with actual implementation
    setTimeout(() => {
      this.isTesting = false;
      this.successMessage = `Tested ${activeEndpoints.length} active endpoints`;
    }, 3000);
  }

  /**
   * Edit endpoint
   */
  editEndpoint(endpoint: ClientEndpointDto): void {
    this.endpointForm.patchValue({
      endpointName: endpoint.endpointName,
      endpointPath: endpoint.endpointPath,
      httpMethod: endpoint.httpMethod,
      isActive: endpoint.isActive,
      // DYNAMIC INTEGRATION: Include system feature in edit
      systemFeature: (endpoint.systemFeature || undefined) as any
    });
    this.showEndpointModal = true;
  }

  /**
   * Import endpoints from OpenAPI specs
   */
  importEndpointsFromSpecs(): void {
    // Mock implementation
    this.successMessage = 'OpenAPI import feature coming soon';
  }

  /**
   * Export endpoint specifications
   */
  exportEndpointSpecs(): void {
    const specs = {
      client: this.selectedClient?.clientName,
      baseUrl: this.selectedClient?.apiBaseUrl,
      endpoints: this.clientEndpoints.map(ep => ({
        name: ep.endpointName,
        path: ep.endpointPath,
        method: ep.httpMethod,
        active: ep.isActive
      })),
      exportedAt: new Date().toISOString()
    };
    
    const blob = new Blob([JSON.stringify(specs, null, 2)], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${this.selectedClient?.clientName || 'client'}-endpoints.json`;
    link.click();
    window.URL.revokeObjectURL(url);
    
    this.successMessage = 'Endpoint specifications exported successfully';
  }

  /**
   * Get method description
   */
  getMethodDescription(method: string): string {
    switch (method.toUpperCase()) {
      case 'GET': return 'Retrieve data';
      case 'POST': return 'Create/send data';
      case 'PUT': return 'Update data';
      case 'PATCH': return 'Partial update';
      case 'DELETE': return 'Remove data';
      default: return 'Custom method';
    }
  }

  /**
   * Get environment badge class
   */
  getEnvironmentBadgeClass(environment?: string): string {
    if (!environment) environment = 'Production';
    
    switch (environment.toLowerCase()) {
      case 'development':
      case 'dev':
        return 'bg-info text-white';
      case 'testing':
      case 'test':
      case 'qa':
        return 'bg-warning text-dark';
      case 'staging':
      case 'stage':
        return 'bg-secondary text-white';
      case 'production':
      case 'prod':
        return 'bg-success text-white';
      default:
        return 'bg-primary text-white';
    }
  }

  /**
   * Get priority badge class
   */
  getPriorityBadgeClass(priority?: number): string {
    if (!priority) priority = 0;
    
    if (priority >= 10) return 'bg-danger text-white';
    if (priority >= 5) return 'bg-warning text-dark';
    if (priority >= 1) return 'bg-info text-white';
    return 'bg-secondary text-white';
  }

  /**
   * Get priority label
   */
  getPriorityLabel(priority?: number): string {
    if (!priority) priority = 0;
    
    if (priority >= 10) return 'CRITICAL';
    if (priority >= 5) return 'HIGH';
    if (priority >= 1) return 'NORMAL';
    return 'LOW';
  }

  /**
   * DYNAMIC INTEGRATION: Get system feature label
   */
  getSystemFeatureLabel(feature?: string): string {
    if (!feature) return 'Not Mapped';
    const featureOption = this.systemFeatureOptions.find(f => f.value === feature);
    return featureOption ? featureOption.label : 'Unknown Feature';
  }

  /**
   * DYNAMIC INTEGRATION: Get system feature icon
   */
  getSystemFeatureIcon(feature?: string): string {
    if (!feature) return 'lucideMinusCircle';
    const featureOption = this.systemFeatureOptions.find(f => f.value === feature);
    return featureOption ? featureOption.icon : 'lucideCircleHelp';
  }

  /**
   * DYNAMIC INTEGRATION: Get system feature description
   */
  getSystemFeatureDescription(feature?: string): string {
    if (!feature) return 'This endpoint is not mapped to any system feature';
    const featureOption = this.systemFeatureOptions.find(f => f.value === feature);
    return featureOption ? featureOption.description : 'Unknown system feature';
  }

  /**
   * DYNAMIC INTEGRATION: Check if feature is already mapped to another endpoint
   */
  isFeatureAlreadyMapped(feature: string, excludeEndpointId?: string): boolean {
    return this.clientEndpoints.some(ep => 
      ep.systemFeature === feature && 
      ep.id !== excludeEndpointId && 
      ep.isActive
    );
  }

  /**
   * DYNAMIC INTEGRATION: Get endpoint by system feature
   */
  getEndpointByFeature(feature: string): ClientEndpointDto | undefined {
    return this.clientEndpoints.find(ep => ep.systemFeature === feature && ep.isActive);
  }

  /**
   * DYNAMIC INTEGRATION: Get unmapped endpoints count
   */
  getUnmappedEndpointsCount(): number {
    return this.clientEndpoints.filter(ep => !ep.systemFeature && ep.isActive).length;
  }

  /**
   * DYNAMIC INTEGRATION: Get mapped features count
   */
  getMappedFeaturesCount(): number {
    return this.clientEndpoints.filter(ep => ep.systemFeature && ep.isActive).length;
  }

  /**
   * Save endpoint mappings - bulk update system feature mappings for all endpoints
   */
  saveEndpointMappings(): void {
    if (!this.selectedClient) {
      this.errorMessage = 'No client selected';
      return;
    }

    console.log('🔄 [saveEndpointMappings] Starting save process for client:', this.selectedClient.id);
    
    // Prepare endpoint mappings for bulk update
    const endpointMappings = this.clientEndpoints
      .map(ep => ({
        endpointId: ep.id,
        systemFeature: ep.systemFeature || null // Allow null for deletions
      }));

    // Allow saving even when all mappings are set to null (deletions)
    // Only prevent save if there are no endpoints at all
    if (this.clientEndpoints.length === 0) {
      this.errorMessage = 'No endpoints found for this client.';
      return;
    }

    console.log('📤 [saveEndpointMappings] Endpoint mappings to save:', endpointMappings);

    this.isSaving = true;
    this.clearMessages();

    this.integrationService.bulkUpdateEndpoints(this.selectedClient.id, endpointMappings)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.isSaving = false)
      )
      .subscribe({
        next: (response: BulkUpdateEndpointsResponse) => {
          console.log('✅ [saveEndpointMappings] Success response:', response);
          
          if (response.success) {
            this.successMessage = `Successfully updated ${response.successfulUpdates} of ${response.totalUpdated} endpoint mappings`;

            // Update original mappings to reflect saved changes
            this.clientEndpoints.forEach(ep => {
              this.originalEndpointMappings.set(ep.id, ep.systemFeature || null);
            });

            // If there were failures, show details
            if (response.failedUpdates > 0) {
              const failedResults = response.results?.filter(r => !r.success) || [];
              const failureDetails = failedResults.map(r => `${r.endpointId}: ${r.message}`).join('; ');
              this.errorMessage = `Failed to update ${response.failedUpdates} mappings: ${failureDetails}`;
            }

            // Refresh client data to show updated mappings
            this.refreshClientData();
          } else {
            this.errorMessage = response.message || 'Failed to save endpoint mappings';
          }
        },
        error: (error: any) => {
          console.error('❌ [saveEndpointMappings] Error:', error);
          
          let errorMsg = 'Failed to save endpoint mappings';
          if (error.status === 400) {
            errorMsg += '. Please check that all endpoint mappings are valid.';
          } else if (error.status === 401) {
            errorMsg += '. Authentication required.';
          } else if (error.status === 403) {
            errorMsg += '. Access denied. Admin permissions required.';
          } else if (error.status === 404) {
            errorMsg += '. Client not found.';
          } else if (error.status >= 500) {
            errorMsg += '. Server error occurred.';
          }
          
          this.errorMessage = errorMsg;
        }
      });
  }

  /**
   * Refresh client data after successful save
   */
  private refreshClientData(): void {
    if (!this.selectedClient) return;
    
    console.log('🔄 [refreshClientData] Refreshing client data after successful save');
    
    // Reload the specific client data
    this.integrationService.getClientIntegration(this.selectedClient.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (updatedClient) => {
          console.log('✅ [refreshClientData] Client data refreshed:', updatedClient);
          
          // Update the selected client and endpoints
          this.selectedClient = updatedClient;
          this.clientEndpoints = updatedClient.endpoints || [];

          // Update original mappings to reflect current state
          this.originalEndpointMappings.clear();
          updatedClient.endpoints.forEach(ep => {
            this.originalEndpointMappings.set(ep.id, ep.systemFeature || null);
          });
          
          // Also refresh the main client list
          this.loadClientIntegrations();
        },
        error: (error) => {
          console.warn('⚠️ [refreshClientData] Failed to refresh client data:', error);
          // Don't show error to user as the save was successful
        }
      });
  }

  /**
   * Refresh client endpoints after CRUD operations
   */
  private refreshClientEndpoints(): void {
    if (!this.selectedClient?.id) return;

    this.integrationService.getClientIntegration(this.selectedClient.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (client) => {
          this.clientEndpoints = client.endpoints || [];
          this.selectedClient = client;
        },
        error: (error) => {
          console.error('Error refreshing endpoints:', error);
          this.errorMessage = 'Failed to refresh endpoints';
        }
      });
  }

  /**
   * Check if there are unsaved endpoint mapping changes
   */
  hasUnsavedEndpointChanges(): boolean {
    if (!this.clientEndpoints) return false;

    return this.clientEndpoints.some(currentEp => {
      const originalFeature = this.originalEndpointMappings.get(currentEp.id);
      return originalFeature !== currentEp.systemFeature;
    });
  }

  /**
   * Update endpoint system feature mapping
   */
  onEndpointFeatureChange(endpoint: ClientEndpointDto, e: Event): void {
    this.updateEndpointFeature(endpoint, (e.target as HTMLSelectElement).value);
  }

  updateEndpointFeature(endpoint: ClientEndpointDto, newFeature: string): void {
    console.log('🔄 [updateEndpointFeature] Updating endpoint feature:', {
      endpointId: endpoint.id,
      endpointName: endpoint.endpointName,
      oldFeature: endpoint.systemFeature,
      newFeature: newFeature
    });
    
    // Update the endpoint's system feature
    endpoint.systemFeature = newFeature as any;
    
    // Show indication that there are unsaved changes
    if (this.hasUnsavedEndpointChanges()) {
      console.log('💾 [updateEndpointFeature] Unsaved changes detected');
    }
  }

  // ===== NEW CREDENTIAL DISPLAY METHODS =====

  /**
   * Check if client has inbound credentials configured
   */
  hasInboundCredentials(): boolean {
    return !!(this.selectedClient?.hasInboundSecret || this.selectedClient?.expectedAccountFromClient);
  }


  /**
   * Get inbound credential status display
   */
  getInboundCredentialStatus(): string {
    if (!this.selectedClient) return 'Unknown';
    
    const hasSecret = this.selectedClient.hasInboundSecret;
    const hasAccount = this.selectedClient.expectedAccountFromClient;
    
    if (hasSecret && hasAccount) return 'Configured';
    if (hasSecret || hasAccount) return 'Partial';
    return 'Not Configured';
  }

  /**
   * Get outbound credential status display
   */
  getOutboundCredentialStatus(): string {
    if (!this.selectedClient) return 'Unknown';
    
    const hasSecret = this.selectedClient.hasOurSecretForClient;
    const hasAccount = this.selectedClient.ourAccountForClient;
    
    if (hasSecret && hasAccount) return 'Configured';
    if (hasSecret || hasAccount) return 'Partial';
    return 'Not Configured';
  }

  /**
   * Get credential status badge class
   */
  getCredentialStatusBadgeClass(status: string): string {
    switch (status) {
      case 'Configured': return 'bg-success text-white';
      case 'Partial': return 'bg-warning text-dark';
      case 'Not Configured': return 'bg-secondary text-white';
      default: return 'bg-light text-dark';
    }
  }

  /**
   * Get integration test status display
   */
  getIntegrationTestStatus(): string {
    if (!this.selectedClient) return 'Never Tested';
    
    if (!this.selectedClient.lastIntegrationTest) return 'Never Tested';
    
    if (this.selectedClient.lastIntegrationTestSuccessful === true) return 'Passed';
    if (this.selectedClient.lastIntegrationTestSuccessful === false) return 'Failed';
    
    return 'Unknown';
  }

  /**
   * Get integration test badge class
   */
  getIntegrationTestBadgeClass(): string {
    const status = this.getIntegrationTestStatus();
    
    switch (status) {
      case 'Passed': return 'bg-success text-white';
      case 'Failed': return 'bg-danger text-white';
      case 'Never Tested': return 'bg-secondary text-white';
      default: return 'bg-light text-dark';
    }
  }

  /**
   * Get environment display name
   */
  getEnvironmentDisplay(environment?: string): string {
    if (!environment) return 'Production';
    
    switch (environment.toLowerCase()) {
      case 'dev':
      case 'development':
        return 'Development';
      case 'test':
      case 'testing':
      case 'qa':
        return 'Testing';
      case 'stage':
      case 'staging':
        return 'Staging';
      case 'prod':
      case 'production':
        return 'Production';
      default:
        return environment.charAt(0).toUpperCase() + environment.slice(1).toLowerCase();
    }
  }

  /**
   * Get configuration priority display
   */
  getConfigurationPriorityDisplay(priority?: number): string {
    if (!priority || priority === 0) return 'Standard';
    
    if (priority >= 10) return 'Critical';
    if (priority >= 5) return 'High';
    if (priority >= 1) return 'Normal';
    
    return 'Low';
  }

  /**
   * Check if client is default for its type
   */
  isDefaultClient(): boolean {
    return this.selectedClient?.isDefaultForClientType === true;
  }

  /**
   * Format last integration test date
   */
  formatLastIntegrationTest(): string {
    if (!this.selectedClient?.lastIntegrationTest) return 'Never';
    
    const date = new Date(this.selectedClient.lastIntegrationTest);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  }

  /**
   * Get credential summary for overview
   */
  getCredentialSummary(): string {
    if (!this.selectedClient) return 'No credentials configured';
    
    const inboundStatus = this.getInboundCredentialStatus();
    const outboundStatus = this.getOutboundCredentialStatus();
    
    if (inboundStatus === 'Configured' && outboundStatus === 'Configured') {
      return 'Bidirectional credentials configured';
    }
    
    if (inboundStatus === 'Configured') {
      return 'Inbound credentials configured';
    }
    
    if (outboundStatus === 'Configured') {
      return 'Outbound credentials configured';
    }
    
    if (inboundStatus === 'Partial' || outboundStatus === 'Partial') {
      return 'Partial credential configuration';
    }
    
    return 'No credentials configured';
  }

}




